import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Portfolio as PortfolioData, AggregatedPosition, AggregatedNav, OpenPosition, NetAssetValue, CashReport } from '../services/api'
import { ArrowLeft, TrendingUp, DollarSign, Briefcase, AlertTriangle, ArrowUp, ArrowDown, ChevronDown, ChevronRight, X, Plus, Minus, ShoppingCart } from 'lucide-react'
import { ColumnResizeProvider, useColumnResize } from '../contexts/ColumnResizeContext'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatValue(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatAccounts(accounts: Set<string> | string[]): string {
  const arr = Array.isArray(accounts) ? accounts : Array.from(accounts)
  if (arr.length === 0) return '-'
  if (arr.length === 1) return arr[0]
  return `${arr.length} accounts`
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-'
  return `${(value * 100).toFixed(1)}%`
}

function convertToUsd(value: number | null | undefined, currency: string | null, fxRateMap: Record<string, number>): number | null {
  if (value === null || value === undefined || isNaN(value)) return null
  if (!currency || currency === 'USD') return value
  const rate = fxRateMap[currency]
  if (!rate) return null
  return value / rate
}

function getUnderlying(symbol: string | null, assetCategory: string, optionUnderlyingMap?: Record<string, string>): string {
  // Only extract underlying for options
  if (!symbol || assetCategory !== 'Equity and Index Options') return ''

  // First, check if we have the underlying from the options table
  if (optionUnderlyingMap && optionUnderlyingMap[symbol]) {
    return optionUnderlyingMap[symbol]
  }

  // Fallback: IBKR format: "CHTR 21JAN28 100 C" -> underlying is "CHTR"
  const parts = symbol.split(' ')
  return parts[0] || ''
}

// Types for exposures view
interface PositionDetail {
  symbol: string | null
  description: string | null
  quantity: number
  price: number | null
  value: number
  currency: string | null
  valueUsd: number | null
  nominalExposure: number | null
  deltaAdjustedExposure: number | null
  ncExposure: number | null
  ncExposureError: string | null
  accountNumbers: string[]
  isOption: boolean
  positionPct: number | null
  nominalExposurePct: number | null
  deltaExposurePct: number | null
  ncExposurePct: number | null
}

interface GroupedExposure {
  underlying: string
  // For determining if expandable
  hasOptions: boolean
  accountNumbers: Set<string>
  // Combined totals
  totalQuantity: number
  totalValue: number
  totalValueUsd: number | null
  totalNominalExposure: number | null
  totalDeltaAdjustedExposure: number | null
  totalNcExposure: number | null
  totalPositionPct: number | null
  totalNominalExposurePct: number | null
  totalDeltaExposurePct: number | null
  totalNcExposurePct: number | null
  hasNcExposureError: boolean  // True if any position has nc_exposure_error
  currencies: Set<string>  // Track all currencies in this group
  // All positions sorted by type (stocks first, then options)
  positions: PositionDetail[]
}

// Futures grouping interface
interface FuturesGroupedExposure {
  underlying: string
  category: 'Futures'
  accountNumbers: Set<string>
  totalQuantity: number
  totalValue: number
  totalValueUsd: number | null
  totalNominalExposure: number | null
  totalDeltaAdjustedExposure: number | null
  totalNcExposure: number | null
  totalPositionPct: number | null
  totalNominalExposurePct: number | null
  totalDeltaExposurePct: number | null
  totalNcExposurePct: number | null
  hasNcExposureError: boolean
  currencies: Set<string>
  positions: PositionDetail[]
}

function groupPositionsByUnderlying(
  positions: OpenPosition[],
  fxRateMap: Record<string, number>,
  optionUnderlyingMap?: Record<string, string>
): { equityAndOptions: GroupedExposure[]; futures: FuturesGroupedExposure[] } {
  const groups: Record<string, GroupedExposure> = {}
  const futuresGroups: Record<string, FuturesGroupedExposure> = {}

  for (const pos of positions) {
    if (pos.row_type !== 'Data' || !pos.symbol) continue

    const isFutures = pos.asset_category === 'Futures'
    const isOptions = pos.asset_category === 'Equity and Index Options'

    // Determine underlying
    let underlying = pos.symbol
    if (isOptions) {
      underlying = getUnderlying(pos.symbol, pos.asset_category, optionUnderlyingMap) || pos.symbol
    }

    const qty = Number(pos.quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.nominal_exposure) || 0
    const deltaExp = Number(pos.delta_adjusted_exposure) || 0
    const posPct = pos.position_pct !== null ? Number(pos.position_pct) : null
    const nominalExpPct = pos.nominal_exposure_pct !== null ? Number(pos.nominal_exposure_pct) : null
    const deltaExpPct = pos.delta_exposure_pct !== null ? Number(pos.delta_exposure_pct) : null
    const ncExp = pos.nc_exposure !== null ? Number(pos.nc_exposure) : null
    const ncExpPct = pos.nc_exposure_pct !== null ? Number(pos.nc_exposure_pct) : null

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.nc_exposure_error || null,
      accountNumbers: pos.account_number ? [pos.account_number] : [],
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (isFutures) {
      const key = underlying
      if (!futuresGroups[key]) {
        futuresGroups[key] = {
          underlying,
          category: 'Futures',
          accountNumbers: new Set(),
          totalQuantity: 0,
          totalValue: 0,
          totalValueUsd: 0,
          totalNominalExposure: 0,
          totalDeltaAdjustedExposure: 0,
          totalNcExposure: 0,
          totalPositionPct: 0,
          totalNominalExposurePct: 0,
          totalDeltaExposurePct: 0,
          totalNcExposurePct: 0,
          hasNcExposureError: false,
          currencies: new Set(),
          positions: [],
        }
      }
      const group = futuresGroups[key]
      if (pos.account_number) group.accountNumbers.add(pos.account_number)
      if (pos.currency) group.currencies.add(pos.currency)
      group.totalQuantity += qty
      group.totalValue += value
      group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
      group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
      group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
      group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
      group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
      group.totalNominalExposurePct = (group.totalNominalExposurePct || 0) + (nominalExpPct || 0)
      group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
      group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
      if (pos.nc_exposure_error) group.hasNcExposureError = true
      group.positions.push(posDetail)
    } else {
      const key = underlying
      if (!groups[key]) {
        groups[key] = {
          underlying,
          hasOptions: false,
          accountNumbers: new Set(),
          totalQuantity: 0,
          totalValue: 0,
          totalValueUsd: 0,
          totalNominalExposure: 0,
          totalDeltaAdjustedExposure: 0,
          totalNcExposure: 0,
          totalPositionPct: 0,
          totalNominalExposurePct: 0,
          totalDeltaExposurePct: 0,
          totalNcExposurePct: 0,
          hasNcExposureError: false,
          currencies: new Set(),
          positions: [],
        }
      }
      const group = groups[key]
      if (pos.account_number) group.accountNumbers.add(pos.account_number)
      if (pos.currency) group.currencies.add(pos.currency)
      if (isOptions) group.hasOptions = true

      group.totalQuantity += qty
      group.totalValue += value
      group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
      group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
      group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
      group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
      group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
      group.totalNominalExposurePct = (group.totalNominalExposurePct || 0) + (nominalExpPct || 0)
      group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
      group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
      if (pos.nc_exposure_error) group.hasNcExposureError = true
      group.positions.push(posDetail)
    }
  }

  // Sort positions within each group: stocks first, then options
  for (const group of Object.values(groups)) {
    group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
  }

  return {
    equityAndOptions: Object.values(groups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0)),
    futures: Object.values(futuresGroups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0)),
  }
}

function groupAggregatePositionsByUnderlying(
  positions: AggregatedPosition[],
  fxRateMap: Record<string, number>,
  accountIdToNumber: Record<number, string>,
  optionUnderlyingMap?: Record<string, string>
): { equityAndOptions: GroupedExposure[]; futures: FuturesGroupedExposure[] } {
  const groups: Record<string, GroupedExposure> = {}
  const futuresGroups: Record<string, FuturesGroupedExposure> = {}

  for (const pos of positions) {
    if (!pos.symbol) continue

    const isFutures = pos.asset_category === 'Futures'
    const isOptions = pos.asset_category === 'Equity and Index Options'

    let underlying = pos.symbol
    if (isOptions) {
      underlying = getUnderlying(pos.symbol, pos.asset_category, optionUnderlyingMap) || pos.symbol
    }

    const qty = Number(pos.total_quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.total_value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.total_nominal_exposure) || 0
    const deltaExp = Number(pos.total_delta_adjusted_exposure) || 0
    const posPct = pos.total_position_pct !== null ? Number(pos.total_position_pct) : null
    const nominalExpPct = pos.total_nominal_exposure_pct !== null ? Number(pos.total_nominal_exposure_pct) : null
    const deltaExpPct = pos.total_delta_exposure_pct !== null ? Number(pos.total_delta_exposure_pct) : null
    const ncExp = pos.total_nc_exposure !== null ? Number(pos.total_nc_exposure) : null
    const ncExpPct = pos.total_nc_exposure_pct !== null ? Number(pos.total_nc_exposure_pct) : null

    // Convert account IDs to account numbers
    const accountNums = (pos.accounts || []).map(id => accountIdToNumber[id] || `#${id}`).filter(Boolean)

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.has_nc_exposure_error ? 'naked_short_call' : null,
      accountNumbers: accountNums,
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (isFutures) {
      const key = underlying
      if (!futuresGroups[key]) {
        futuresGroups[key] = {
          underlying,
          category: 'Futures',
          accountNumbers: new Set(),
          totalQuantity: 0,
          totalValue: 0,
          totalValueUsd: 0,
          totalNominalExposure: 0,
          totalDeltaAdjustedExposure: 0,
          totalNcExposure: 0,
          totalPositionPct: 0,
          totalNominalExposurePct: 0,
          totalDeltaExposurePct: 0,
          totalNcExposurePct: 0,
          hasNcExposureError: false,
          currencies: new Set(),
          positions: [],
        }
      }
      const group = futuresGroups[key]
      accountNums.forEach(a => group.accountNumbers.add(a))
      if (pos.currency) group.currencies.add(pos.currency)
      group.totalQuantity += qty
      group.totalValue += value
      group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
      group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
      group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
      group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
      group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
      group.totalNominalExposurePct = (group.totalNominalExposurePct || 0) + (nominalExpPct || 0)
      group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
      group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
      if (pos.has_nc_exposure_error) group.hasNcExposureError = true
      group.positions.push(posDetail)
    } else {
      const key = underlying
      if (!groups[key]) {
        groups[key] = {
          underlying,
          hasOptions: false,
          accountNumbers: new Set(),
          totalQuantity: 0,
          totalValue: 0,
          totalValueUsd: 0,
          totalNominalExposure: 0,
          totalDeltaAdjustedExposure: 0,
          totalNcExposure: 0,
          totalPositionPct: 0,
          totalNominalExposurePct: 0,
          totalDeltaExposurePct: 0,
          totalNcExposurePct: 0,
          hasNcExposureError: false,
          currencies: new Set(),
          positions: [],
        }
      }
      const group = groups[key]
      accountNums.forEach(a => group.accountNumbers.add(a))
      if (pos.currency) group.currencies.add(pos.currency)
      if (isOptions) group.hasOptions = true

      group.totalQuantity += qty
      group.totalValue += value
      group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
      group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
      group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
      group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
      group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
      group.totalNominalExposurePct = (group.totalNominalExposurePct || 0) + (nominalExpPct || 0)
      group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
      group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
      if (pos.has_nc_exposure_error) group.hasNcExposureError = true
      group.positions.push(posDetail)
    }
  }

  // Sort positions within each group: stocks first, then options
  for (const group of Object.values(groups)) {
    group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
  }

  return {
    equityAndOptions: Object.values(groups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0)),
    futures: Object.values(futuresGroups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0)),
  }
}

// By Currency grouping interface
interface CurrencyGroupedExposure {
  currency: string
  accountNumbers: Set<string>
  totalValue: number
  totalValueUsd: number | null
  totalNominalExposure: number | null
  totalDeltaAdjustedExposure: number | null
  totalNcExposure: number | null
  totalPositionPct: number | null
  totalNominalExposurePct: number | null
  totalDeltaExposurePct: number | null
  totalNcExposurePct: number | null
  positionCount: number
  positions: PositionDetail[]
}

// By Type grouping interface
interface TypeGroupedExposure {
  assetType: 'Equity' | 'Options' | 'Futures'
  accountNumbers: Set<string>
  totalValue: number
  totalValueUsd: number | null
  totalNominalExposure: number | null
  totalDeltaAdjustedExposure: number | null
  totalNcExposure: number | null
  totalPositionPct: number | null
  totalNominalExposurePct: number | null
  totalDeltaExposurePct: number | null
  totalNcExposurePct: number | null
  currencies: Set<string>
  positionCount: number
  positions: PositionDetail[]
}

function groupPositionsByCurrency(
  positions: OpenPosition[],
  fxRateMap: Record<string, number>
): CurrencyGroupedExposure[] {
  const groups: Record<string, CurrencyGroupedExposure> = {}

  for (const pos of positions) {
    if (pos.row_type !== 'Data' || !pos.symbol) continue
    // Only include Equity and Options, not Futures
    if (pos.asset_category === 'Futures') continue

    const currency = pos.currency || 'Unknown'
    const qty = Number(pos.quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.nominal_exposure) || 0
    const deltaExp = Number(pos.delta_adjusted_exposure) || 0
    const posPct = pos.position_pct !== null ? Number(pos.position_pct) : null
    const nominalExpPct = pos.nominal_exposure_pct !== null ? Number(pos.nominal_exposure_pct) : null
    const deltaExpPct = pos.delta_exposure_pct !== null ? Number(pos.delta_exposure_pct) : null
    const ncExp = pos.nc_exposure !== null ? Number(pos.nc_exposure) : null
    const ncExpPct = pos.nc_exposure_pct !== null ? Number(pos.nc_exposure_pct) : null
    const isOptions = pos.asset_category === 'Equity and Index Options'

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.nc_exposure_error || null,
      accountNumbers: pos.account_number ? [pos.account_number] : [],
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (!groups[currency]) {
      groups[currency] = {
        currency,
        accountNumbers: new Set(),
        totalValue: 0,
        totalValueUsd: 0,
        totalNominalExposure: 0,
        totalDeltaAdjustedExposure: 0,
        totalNcExposure: 0,
        totalPositionPct: 0,
        totalNominalExposurePct: 0,
        totalDeltaExposurePct: 0,
        totalNcExposurePct: 0,
        positionCount: 0,
        positions: [],
      }
    }
    const group = groups[currency]
    if (pos.account_number) group.accountNumbers.add(pos.account_number)
    group.totalValue += value
    group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
    group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
    group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
    group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
    group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
    group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
    group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
    group.positionCount += 1
    group.positions.push(posDetail)
  }

  return Object.values(groups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0))
}

function groupAggregatePositionsByCurrency(
  positions: AggregatedPosition[],
  fxRateMap: Record<string, number>,
  accountIdToNumber: Record<number, string>
): CurrencyGroupedExposure[] {
  const groups: Record<string, CurrencyGroupedExposure> = {}

  for (const pos of positions) {
    if (!pos.symbol) continue
    // Only include Equity and Options, not Futures
    if (pos.asset_category === 'Futures') continue

    const currency = pos.currency || 'Unknown'
    const qty = Number(pos.total_quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.total_value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.total_nominal_exposure) || 0
    const deltaExp = Number(pos.total_delta_adjusted_exposure) || 0
    const posPct = pos.total_position_pct !== null ? Number(pos.total_position_pct) : null
    const nominalExpPct = pos.total_nominal_exposure_pct !== null ? Number(pos.total_nominal_exposure_pct) : null
    const deltaExpPct = pos.total_delta_exposure_pct !== null ? Number(pos.total_delta_exposure_pct) : null
    const ncExp = pos.total_nc_exposure !== null ? Number(pos.total_nc_exposure) : null
    const ncExpPct = pos.total_nc_exposure_pct !== null ? Number(pos.total_nc_exposure_pct) : null
    const accountNums = (pos.accounts || []).map(id => accountIdToNumber[id] || `#${id}`).filter(Boolean)
    const isOptions = pos.asset_category === 'Equity and Index Options'

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.has_nc_exposure_error ? 'naked_short_call' : null,
      accountNumbers: accountNums,
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (!groups[currency]) {
      groups[currency] = {
        currency,
        accountNumbers: new Set(),
        totalValue: 0,
        totalValueUsd: 0,
        totalNominalExposure: 0,
        totalDeltaAdjustedExposure: 0,
        totalNcExposure: 0,
        totalPositionPct: 0,
        totalNominalExposurePct: 0,
        totalDeltaExposurePct: 0,
        totalNcExposurePct: 0,
        positionCount: 0,
        positions: [],
      }
    }
    const group = groups[currency]
    accountNums.forEach(a => group.accountNumbers.add(a))
    group.totalValue += value
    group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
    group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
    group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
    group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
    group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
    group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
    group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
    group.positionCount += 1
    group.positions.push(posDetail)
  }

  return Object.values(groups).sort((a, b) => (b.totalValueUsd || 0) - (a.totalValueUsd || 0))
}

function groupPositionsByType(
  positions: OpenPosition[],
  fxRateMap: Record<string, number>
): TypeGroupedExposure[] {
  const groups: Record<string, TypeGroupedExposure> = {}

  for (const pos of positions) {
    if (pos.row_type !== 'Data' || !pos.symbol) continue

    let assetType: 'Equity' | 'Options' | 'Futures'
    if (pos.asset_category === 'Futures') {
      assetType = 'Futures'
    } else if (pos.asset_category === 'Equity and Index Options') {
      assetType = 'Options'
    } else {
      assetType = 'Equity'
    }

    const qty = Number(pos.quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.nominal_exposure) || 0
    const deltaExp = Number(pos.delta_adjusted_exposure) || 0
    const posPct = pos.position_pct !== null ? Number(pos.position_pct) : null
    const nominalExpPct = pos.nominal_exposure_pct !== null ? Number(pos.nominal_exposure_pct) : null
    const deltaExpPct = pos.delta_exposure_pct !== null ? Number(pos.delta_exposure_pct) : null
    const ncExp = pos.nc_exposure !== null ? Number(pos.nc_exposure) : null
    const ncExpPct = pos.nc_exposure_pct !== null ? Number(pos.nc_exposure_pct) : null
    const isOptions = pos.asset_category === 'Equity and Index Options'

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.nc_exposure_error || null,
      accountNumbers: pos.account_number ? [pos.account_number] : [],
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (!groups[assetType]) {
      groups[assetType] = {
        assetType,
        accountNumbers: new Set(),
        totalValue: 0,
        totalValueUsd: 0,
        totalNominalExposure: 0,
        totalDeltaAdjustedExposure: 0,
        totalNcExposure: 0,
        totalPositionPct: 0,
        totalNominalExposurePct: 0,
        totalDeltaExposurePct: 0,
        totalNcExposurePct: 0,
        currencies: new Set(),
        positionCount: 0,
        positions: [],
      }
    }
    const group = groups[assetType]
    if (pos.account_number) group.accountNumbers.add(pos.account_number)
    if (pos.currency) group.currencies.add(pos.currency)
    group.totalValue += value
    group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
    group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
    group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
    group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
    group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
    group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
    group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
    group.positionCount += 1
    group.positions.push(posDetail)
  }

  // Sort by order: Equity, Options, Futures
  const order: Record<string, number> = { Equity: 0, Options: 1, Futures: 2 }
  return Object.values(groups).sort((a, b) => order[a.assetType] - order[b.assetType])
}

function groupAggregatePositionsByType(
  positions: AggregatedPosition[],
  fxRateMap: Record<string, number>,
  accountIdToNumber: Record<number, string>
): TypeGroupedExposure[] {
  const groups: Record<string, TypeGroupedExposure> = {}

  for (const pos of positions) {
    if (!pos.symbol) continue

    let assetType: 'Equity' | 'Options' | 'Futures'
    if (pos.asset_category === 'Futures') {
      assetType = 'Futures'
    } else if (pos.asset_category === 'Equity and Index Options') {
      assetType = 'Options'
    } else {
      assetType = 'Equity'
    }

    const qty = Number(pos.total_quantity) || 0
    const price = Number(pos.close_price) || null
    const value = Number(pos.total_value) || 0
    const valueUsd = convertToUsd(value, pos.currency, fxRateMap)
    const nominalExp = Number(pos.total_nominal_exposure) || 0
    const deltaExp = Number(pos.total_delta_adjusted_exposure) || 0
    const posPct = pos.total_position_pct !== null ? Number(pos.total_position_pct) : null
    const nominalExpPct = pos.total_nominal_exposure_pct !== null ? Number(pos.total_nominal_exposure_pct) : null
    const deltaExpPct = pos.total_delta_exposure_pct !== null ? Number(pos.total_delta_exposure_pct) : null
    const ncExp = pos.total_nc_exposure !== null ? Number(pos.total_nc_exposure) : null
    const ncExpPct = pos.total_nc_exposure_pct !== null ? Number(pos.total_nc_exposure_pct) : null
    const accountNums = (pos.accounts || []).map(id => accountIdToNumber[id] || `#${id}`).filter(Boolean)
    const isOptions = pos.asset_category === 'Equity and Index Options'

    const posDetail: PositionDetail = {
      symbol: pos.symbol,
      description: pos.description,
      quantity: qty,
      price,
      value,
      currency: pos.currency,
      valueUsd,
      nominalExposure: nominalExp,
      deltaAdjustedExposure: deltaExp,
      ncExposure: ncExp,
      ncExposureError: pos.has_nc_exposure_error ? 'naked_short_call' : null,
      accountNumbers: accountNums,
      isOption: isOptions,
      positionPct: posPct,
      nominalExposurePct: nominalExpPct,
      deltaExposurePct: deltaExpPct,
      ncExposurePct: ncExpPct,
    }

    if (!groups[assetType]) {
      groups[assetType] = {
        assetType,
        accountNumbers: new Set(),
        totalValue: 0,
        totalValueUsd: 0,
        totalNominalExposure: 0,
        totalDeltaAdjustedExposure: 0,
        totalNcExposure: 0,
        totalPositionPct: 0,
        totalNominalExposurePct: 0,
        totalDeltaExposurePct: 0,
        totalNcExposurePct: 0,
        currencies: new Set(),
        positionCount: 0,
        positions: [],
      }
    }
    const group = groups[assetType]
    accountNums.forEach(a => group.accountNumbers.add(a))
    if (pos.currency) group.currencies.add(pos.currency)
    group.totalValue += value
    group.totalValueUsd = (group.totalValueUsd || 0) + (valueUsd || 0)
    group.totalNominalExposure = (group.totalNominalExposure || 0) + nominalExp
    group.totalDeltaAdjustedExposure = (group.totalDeltaAdjustedExposure || 0) + deltaExp
    group.totalNcExposure = (group.totalNcExposure || 0) + (ncExp || 0)
    group.totalPositionPct = (group.totalPositionPct || 0) + (posPct || 0)
    group.totalDeltaExposurePct = (group.totalDeltaExposurePct || 0) + (deltaExpPct || 0)
    group.totalNcExposurePct = (group.totalNcExposurePct || 0) + (ncExpPct || 0)
    group.positionCount += 1
    group.positions.push(posDetail)
  }

  // Sort by order: Equity, Options, Futures
  const order: Record<string, number> = { Equity: 0, Options: 1, Futures: 2 }
  return Object.values(groups).sort((a, b) => order[a.assetType] - order[b.assetType])
}

// Sorting types and utilities
type SortDirection = 'asc' | 'desc' | null
type SortConfig<T> = { key: keyof T | string; direction: SortDirection }

function SortableHeader<T>({
  label,
  sortKey,
  sortConfig,
  onSort,
  className = '',
  tableKey,
  columnKey,
  defaultWidth = 100,
}: {
  label: string
  sortKey: keyof T | string
  sortConfig: SortConfig<T>
  onSort: (key: keyof T | string) => void
  className?: string
  tableKey?: string
  columnKey?: string
  defaultWidth?: number
}) {
  const { getWidth, getResizeProps, isResizing } = useColumnResize()
  const isActive = sortConfig.key === sortKey && sortConfig.direction !== null
  const colKey = columnKey || String(sortKey)
  const width = tableKey ? getWidth(tableKey, colKey, defaultWidth) : undefined
  const resizeProps = tableKey ? getResizeProps(tableKey, colKey) : null

  const handleClick = (e: React.MouseEvent) => {
    // Don't sort if clicking on the resize handle area (right 8px)
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const isResizeArea = clickX > rect.width - 8
    if (isResizeArea || isResizing) return
    onSort(sortKey)
  }

  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none overflow-hidden ${className}`}
      onClick={handleClick}
      style={width ? { width: `${width}px`, minWidth: '20px', maxWidth: `${width}px`, position: 'relative' } : { position: 'relative' }}
    >
      <div className={`flex items-center gap-1 overflow-hidden whitespace-nowrap ${className.includes('text-right') ? 'justify-end' : ''}`}>
        <span className="truncate overflow-hidden">{label}</span>
        <span className="w-4 h-4 inline-flex items-center justify-center flex-shrink-0">
          {isActive && sortConfig.direction === 'asc' && <ArrowUp className="h-3 w-3" />}
          {isActive && sortConfig.direction === 'desc' && <ArrowDown className="h-3 w-3" />}
        </span>
      </div>
      {resizeProps && (
        <div
          {...resizeProps}
          onClick={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-400"
          style={{ zIndex: 2 }}
        />
      )}
    </th>
  )
}

// Non-sortable resizable header
function ResizableHeader({
  children,
  className = '',
  tableKey,
  columnKey,
  defaultWidth = 100,
}: {
  children: React.ReactNode
  className?: string
  tableKey: string
  columnKey: string
  defaultWidth?: number
}) {
  const { getWidth, getResizeProps } = useColumnResize()
  const width = getWidth(tableKey, columnKey, defaultWidth)
  const resizeProps = getResizeProps(tableKey, columnKey)

  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase overflow-hidden ${className}`}
      style={{ width: `${width}px`, minWidth: '20px', maxWidth: `${width}px`, position: 'relative' }}
    >
      <span className="truncate block overflow-hidden whitespace-nowrap">{children}</span>
      <div
        {...resizeProps}
        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400"
        style={{ zIndex: 2 }}
      />
    </th>
  )
}

function sortData<T>(data: T[], sortConfig: SortConfig<T>): T[] {
  if (!sortConfig.direction) return data

  return [...data].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortConfig.key as string]
    const bVal = (b as unknown as Record<string, unknown>)[sortConfig.key as string]

    // Handle null/undefined
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1

    // Compare values - handle numeric strings from API (Decimal serialization)
    let comparison = 0
    const aNum = typeof aVal === 'number' ? aVal : (typeof aVal === 'string' ? parseFloat(aVal) : NaN)
    const bNum = typeof bVal === 'number' ? bVal : (typeof bVal === 'string' ? parseFloat(bVal) : NaN)

    if (!isNaN(aNum) && !isNaN(bNum)) {
      comparison = aNum - bNum
    } else {
      comparison = String(aVal).localeCompare(String(bVal))
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison
  })
}

function useSorting<T>(initialKey: keyof T | string = ''): [SortConfig<T>, (key: keyof T | string) => void] {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({ key: initialKey, direction: null })

  const handleSort = (key: keyof T | string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, direction: 'asc' }
      }
      // Cycle: null -> asc -> desc -> null
      if (prev.direction === null) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key, direction: null }
    })
  }

  return [sortConfig, handleSort]
}

// Calculate summary values from portfolio data
function calculateSummary(portfolio: PortfolioData | undefined) {
  const defaultSummary = { totalValue: 0, securities: 0, cash: 0, navTotal: 0, calculatedTotal: 0, hasError: false }
  if (!portfolio) return defaultSummary

  // Find the Total row in NAV for overall value
  // Data has row_type='Data' with asset_class='Total' for totals
  const navTotalRow = portfolio.net_asset_values.find(
    nav => nav.row_type === 'Data' && nav.asset_class === 'Total'
  )
  const navTotal = Number(navTotalRow?.current_total) || 0

  // Cash from Ending Cash line in Base Currency Summary
  // Look for "Ending Cash" line with "Base Currency Summary" currency
  const endingCashRow = portfolio.cash_reports.find(
    cash => cash.line_item === 'Ending Cash' && cash.currency === 'Base Currency Summary'
  )
  const cash = Number(endingCashRow?.total) || 0

  // Securities from NAV - all non-cash asset classes
  // Sum all Data rows except Cash and Total
  const securities = portfolio.net_asset_values
    .filter(nav => nav.row_type === 'Data' && nav.asset_class !== 'Cash' && nav.asset_class !== 'Total')
    .reduce((sum, nav) => sum + (Number(nav.current_total) || 0), 0)

  // Total should be securities + cash
  const calculatedTotal = securities + cash

  // Check for mismatch (allow small rounding differences)
  const hasError = navTotal > 0 && Math.abs(calculatedTotal - navTotal) > 0.01

  return {
    totalValue: navTotal,
    securities,
    cash,
    navTotal,
    calculatedTotal,
    hasError,
  }
}

// Check if a cash row is the important "Ending Cash" line
function isEndingCashRow(cash: CashReport): boolean {
  return cash.line_item === 'Ending Cash' && cash.currency === 'Base Currency Summary'
}

type Tab = 'agg-positions' | 'agg-exposures' | 'agg-comparison' | 'agg-nominal-comparison' | 'agg-delta-comparison' | 'agg-nc-comparison' | 'agg-nav' | 'agg-cash' | 'acct-positions' | 'acct-exposures' | 'acct-nav' | 'acct-cash'

// Types for Position Comparison view
interface ComparisonPositionDetail {
  symbol: string
  isOption: boolean
  accountPcts: Record<string, number | null> // accountNumber -> position_pct
}

interface ComparisonGroup {
  underlying: string
  hasOptions: boolean
  accountPcts: Record<string, number | null> // accountNumber -> sum of position_pcts
  positions: ComparisonPositionDetail[]
}

// Extended types with computed USD value for sorting
type AggregatedPositionWithUsd = AggregatedPosition & { value_usd: number | null }
type OpenPositionWithUsd = OpenPosition & { value_usd: number | null }

function PortfolioContent() {
  const { uploadId } = useParams<{ uploadId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') as Tab | null
  const [activeTab, setActiveTabState] = useState<Tab>(tabFromUrl || 'agg-positions')
  const [selectedAccount, setSelectedAccount] = useState<number | 'all'>('all')

  // Sync tab state with URL
  const setActiveTab = (tab: Tab) => {
    setActiveTabState(tab)
    setSearchParams({ tab })
  }

  // Initialize tab from URL on mount
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // When individual account selected, aggregate tabs are disabled
  const isAggregateDisabled = selectedAccount !== 'all'

  // Switch away from aggregate tabs when selecting individual account
  const handleAccountChange = (value: 'all' | number) => {
    setSelectedAccount(value)
    if (value !== 'all' && activeTab.startsWith('agg-')) {
      setActiveTab('acct-positions')
    }
  }

  // Sorting state for each table
  const [aggPositionsSort, setAggPositionsSort] = useSorting<AggregatedPositionWithUsd>('total_value')
  const [aggNavSort, setAggNavSort] = useSorting<AggregatedNav>('asset_class')
  const [aggCashSort, setAggCashSort] = useSorting<{ line_item: string; currency: string | null; total: number | null }>('line_item')
  const [acctPositionsSort, setAcctPositionsSort] = useSorting<OpenPositionWithUsd>('value')
  const [acctNavSort, setAcctNavSort] = useSorting<NetAssetValue>('asset_class')
  const [acctCashSort, setAcctCashSort] = useSorting<CashReport>('line_item')

  // Exposures view state
  const [expandedExposures, setExpandedExposures] = useState<Set<string>>(new Set())
  const [exposuresSort, setExposuresSort] = useSorting<GroupedExposure>('totalValueUsd')
  const [futuresSort, setFuturesSort] = useSorting<FuturesGroupedExposure>('totalValueUsd')
  const [currencySort, setCurrencySort] = useSorting<CurrencyGroupedExposure>('totalValueUsd')
  const [typeSort, setTypeSort] = useSorting<TypeGroupedExposure>('totalValueUsd')
  const toggleExpanded = (key: string) => {
    setExpandedExposures(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['agg-byUnderlying', 'acct-byUnderlying']))
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }

  // Excluded accounts management
  const [showExcludedAccounts, setShowExcludedAccounts] = useState(false)
  const queryClient = useQueryClient()

  // Position Comparison sorting state
  const [comparisonAccountSort, setComparisonAccountSort] = useState<'number' | 'value'>('value')
  const [comparisonRowSort, setComparisonRowSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: 'wtdAvg', direction: 'desc' })

  // Delta Exposure Comparison sorting state
  const [deltaComparisonAccountSort, setDeltaComparisonAccountSort] = useState<'number' | 'value'>('value')
  const [deltaComparisonRowSort, setDeltaComparisonRowSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: 'wtdAvg', direction: 'desc' })

  // Nominal Exposure Comparison sorting state
  const [nominalComparisonAccountSort, setNominalComparisonAccountSort] = useState<'number' | 'value'>('value')
  const [nominalComparisonRowSort, setNominalComparisonRowSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: 'wtdAvg', direction: 'desc' })

  // NC Exposure Comparison sorting state
  const [ncComparisonAccountSort, setNcComparisonAccountSort] = useState<'number' | 'value'>('value')
  const [ncComparisonRowSort, setNcComparisonRowSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: 'wtdAvg', direction: 'desc' })

  // Subsector state - persisted to localStorage
  const [selectedSubsectorId, setSelectedSubsectorId] = useState<number | null>(() => {
    const stored = localStorage.getItem('selectedSubsectorId')
    return stored ? Number(stored) : null
  })
  const [showCreateSubsectorModal, setShowCreateSubsectorModal] = useState(false)
  const [newSubsectorName, setNewSubsectorName] = useState('')

  // Persist subsector selection to localStorage
  useEffect(() => {
    if (selectedSubsectorId !== null) {
      localStorage.setItem('selectedSubsectorId', String(selectedSubsectorId))
    } else {
      localStorage.removeItem('selectedSubsectorId')
    }
  }, [selectedSubsectorId])

  const toggleExclusionMutation = useMutation({
    mutationFn: (accountId: number) => api.toggleAccountExclusion(accountId),
    onSuccess: () => {
      // Invalidate queries that depend on account exclusion
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['aggregate'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
    },
  })

  const { data: upload, isLoading: uploadLoading } = useQuery({
    queryKey: ['upload', uploadId],
    queryFn: () => api.getUpload(Number(uploadId)),
    enabled: !!uploadId,
  })

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.getAccounts(),
  })

  const { data: aggregate, isLoading: aggregateLoading } = useQuery({
    queryKey: ['aggregate', uploadId],
    queryFn: () => api.getAggregate(Number(uploadId)),
    enabled: !!uploadId,
  })

  // Always fetch portfolio data for the selected account (needed for summary cards)
  const { data: portfolio, isLoading: portfolioLoading } = useQuery({
    queryKey: ['portfolio', uploadId, selectedAccount],
    queryFn: () =>
      api.getPortfolio(
        Number(uploadId),
        selectedAccount !== 'all' ? selectedAccount : undefined
      ),
    enabled: !!uploadId,
  })

  const { data: metrics } = useQuery({
    queryKey: ['metrics', uploadId],
    queryFn: () => api.getMetrics(Number(uploadId)),
    enabled: !!uploadId,
  })

  const { data: fxRates } = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => api.getFxRates('USD'),
  })

  // Fetch options to build symbol -> underlying mapping
  const { data: optionsData } = useQuery({
    queryKey: ['options'],
    queryFn: () => api.getOptions(),
  })

  // Fetch subsectors for comparison page filtering
  const { data: subsectorsData } = useQuery({
    queryKey: ['subsectors'],
    queryFn: () => api.getSubsectors(),
  })

  // Subsector mutations
  const createSubsectorMutation = useMutation({
    mutationFn: (name: string) => api.createSubsector(name),
    onSuccess: (newSubsector) => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
      setSelectedSubsectorId(newSubsector.id)
      setShowCreateSubsectorModal(false)
      setNewSubsectorName('')
    },
  })

  const addToSubsectorMutation = useMutation({
    mutationFn: ({ subsectorId, underlying }: { subsectorId: number; underlying: string }) =>
      api.addUnderlyingToSubsector(subsectorId, underlying),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
    },
  })

  const removeFromSubsectorMutation = useMutation({
    mutationFn: ({ subsectorId, underlying }: { subsectorId: number; underlying: string }) =>
      api.removeUnderlyingFromSubsector(subsectorId, underlying),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subsectors'] })
    },
  })

  // Get the currently selected subsector
  const selectedSubsector = useMemo(() => {
    if (!selectedSubsectorId || !subsectorsData?.subsectors) return null
    return subsectorsData.subsectors.find(s => s.id === selectedSubsectorId) || null
  }, [selectedSubsectorId, subsectorsData])

  // Get set of underlyings in the selected subsector for quick lookup
  const underlyingsInSubsector = useMemo(() => {
    if (!selectedSubsector) return new Set<string>()
    return new Set(selectedSubsector.underlyings.map(u => u.underlying))
  }, [selectedSubsector])

  // Create a map for quick FX rate lookup
  const fxRateMap = useMemo(() => {
    const map: Record<string, number> = { USD: 1 }
    fxRates?.forEach(rate => {
      map[rate.quote_currency] = Number(rate.rate)
    })
    return map
  }, [fxRates])

  // Create a map for option symbol -> underlying lookup
  const optionUnderlyingMap = useMemo(() => {
    const map: Record<string, string> = {}
    optionsData?.options.forEach(opt => {
      // Map the option symbol (e.g., "CMCS1 21JAN28 100 C") to its underlying (e.g., "CMCSA")
      map[opt.symbol] = opt.underlying
    })
    return map
  }, [optionsData])

  // Create a map for account ID to account number lookup
  const accountIdToNumber = useMemo(() => {
    const map: Record<number, string> = {}
    accounts?.accounts.forEach(acct => {
      map[acct.id] = acct.account_number
    })
    return map
  }, [accounts])

  // Calculate summary based on selected account
  const summary = useMemo(() => {
    if (selectedAccount === 'all') {
      // Use aggregate metrics for all accounts
      const totalValue = metrics?.total_portfolio_value ?? 0
      const securities = metrics?.total_securities_value ?? 0
      const cash = metrics?.total_cash ?? 0
      const calculatedTotal = securities + cash
      const hasError = Math.abs(calculatedTotal - totalValue) > 0.01
      return {
        totalValue,
        securities,
        cash,
        navTotal: totalValue,
        calculatedTotal,
        hasError,
      }
    } else {
      // Calculate from individual account's portfolio data
      return calculateSummary(portfolio)
    }
  }, [selectedAccount, metrics, portfolio])

  // Get calculated_total_usd from backend (positions Eq+Opt USD + Cash)
  const calculatedTotalUsd = useMemo(() => {
    if (selectedAccount === 'all' && aggregate) {
      return aggregate.calculated_total_usd
    } else if (portfolio) {
      // Find the Total row in net_asset_values
      const totalRow = portfolio.net_asset_values.find(
        nav => nav.asset_class === 'Total' && nav.row_type === 'Data'
      )
      return totalRow?.calculated_total_usd ?? null
    }
    return null
  }, [selectedAccount, aggregate, portfolio])

  // Get non-excluded accounts for Position Comparison view
  const includedAccounts = useMemo(() => {
    return accounts?.accounts.filter(a => !a.exclude_from_aggregate) || []
  }, [accounts])

  // Fetch portfolio data for all included accounts (for Position Comparison)
  // staleTime of 5 minutes keeps data cached to avoid refetching when navigating back
  const allAccountPortfolios = useQueries({
    queries: includedAccounts.map(account => ({
      queryKey: ['portfolio', uploadId, account.id],
      queryFn: () => api.getPortfolio(Number(uploadId), account.id),
      enabled: !!uploadId && (activeTab === 'agg-comparison' || activeTab === 'agg-nominal-comparison' || activeTab === 'agg-delta-comparison' || activeTab === 'agg-nc-comparison'),
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Get calculated_total_usd for each account
  const accountCalculatedTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    allAccountPortfolios.forEach((result, idx) => {
      if (result.data) {
        const acctNum = includedAccounts[idx]?.account_number
        const totalRow = result.data.net_asset_values.find(
          nav => nav.asset_class === 'Total' && nav.row_type === 'Data'
        )
        if (acctNum && totalRow?.calculated_total_usd) {
          totals[acctNum] = Number(totalRow.calculated_total_usd)
        }
      }
    })
    return totals
  }, [allAccountPortfolios, includedAccounts])

  // Build comparison data structure
  const comparisonData = useMemo(() => {
    if (!includedAccounts.length || allAccountPortfolios.some(q => q.isLoading)) {
      return { groups: [] as ComparisonGroup[], accountNumbers: [] as string[] }
    }

    const accountNumbers = includedAccounts.map(a => a.account_number)
    const groups: Record<string, ComparisonGroup> = {}

    // Process each account's positions
    allAccountPortfolios.forEach((result, idx) => {
      if (!result.data) return
      const acctNum = includedAccounts[idx]?.account_number
      if (!acctNum) return

      const positions = result.data.open_positions.filter(
        p => p.row_type === 'Data' && p.symbol && p.asset_category !== 'Futures'
      )

      for (const pos of positions) {
        const isOptions = pos.asset_category === 'Equity and Index Options'
        let underlying = pos.symbol || ''
        if (isOptions) {
          underlying = getUnderlying(pos.symbol || '', pos.asset_category) || pos.symbol || ''
        }

        // Initialize group if needed
        if (!groups[underlying]) {
          groups[underlying] = {
            underlying,
            hasOptions: false,
            accountPcts: {},
            positions: [],
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            groups[underlying].accountPcts[an] = null
          })
        }

        const group = groups[underlying]
        if (isOptions) group.hasOptions = true

        // Add position percentage for this account
        const posPct = pos.position_pct !== null ? Number(pos.position_pct) : null
        group.accountPcts[acctNum] = (group.accountPcts[acctNum] || 0) + (posPct || 0)

        // Find or create position detail
        let posDetail = group.positions.find(p => p.symbol === pos.symbol)
        if (!posDetail) {
          posDetail = {
            symbol: pos.symbol || '',
            isOption: isOptions,
            accountPcts: {},
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            posDetail!.accountPcts[an] = null
          })
          group.positions.push(posDetail)
        }
        posDetail.accountPcts[acctNum] = posPct
      }
    })

    // Sort groups by total value and positions within groups
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const aTotal = Object.values(a.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      const bTotal = Object.values(b.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      return bTotal - aTotal
    })

    // Sort positions within each group: stocks first, then options
    sortedGroups.forEach(group => {
      group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
    })

    return { groups: sortedGroups, accountNumbers }
  }, [includedAccounts, allAccountPortfolios])

  // Build delta exposure comparison data structure (same as above but using delta_exposure_pct)
  const deltaComparisonData = useMemo(() => {
    if (!includedAccounts.length || allAccountPortfolios.some(q => q.isLoading)) {
      return { groups: [] as ComparisonGroup[], accountNumbers: [] as string[] }
    }

    const accountNumbers = includedAccounts.map(a => a.account_number)
    const groups: Record<string, ComparisonGroup> = {}

    // Process each account's positions
    allAccountPortfolios.forEach((result, idx) => {
      if (!result.data) return
      const acctNum = includedAccounts[idx]?.account_number
      if (!acctNum) return

      const positions = result.data.open_positions.filter(
        p => p.row_type === 'Data' && p.symbol && p.asset_category !== 'Futures'
      )

      for (const pos of positions) {
        const isOptions = pos.asset_category === 'Equity and Index Options'
        let underlying = pos.symbol || ''
        if (isOptions) {
          underlying = getUnderlying(pos.symbol || '', pos.asset_category) || pos.symbol || ''
        }

        // Initialize group if needed
        if (!groups[underlying]) {
          groups[underlying] = {
            underlying,
            hasOptions: false,
            accountPcts: {},
            positions: [],
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            groups[underlying].accountPcts[an] = null
          })
        }

        const group = groups[underlying]
        if (isOptions) group.hasOptions = true

        // Add delta exposure percentage for this account
        const deltaPct = pos.delta_exposure_pct !== null ? Number(pos.delta_exposure_pct) : null
        group.accountPcts[acctNum] = (group.accountPcts[acctNum] || 0) + (deltaPct || 0)

        // Find or create position detail
        let posDetail = group.positions.find(p => p.symbol === pos.symbol)
        if (!posDetail) {
          posDetail = {
            symbol: pos.symbol || '',
            isOption: isOptions,
            accountPcts: {},
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            posDetail!.accountPcts[an] = null
          })
          group.positions.push(posDetail)
        }
        posDetail.accountPcts[acctNum] = deltaPct
      }
    })

    // Sort groups by total value and positions within groups
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const aTotal = Object.values(a.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      const bTotal = Object.values(b.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      return bTotal - aTotal
    })

    // Sort positions within each group: stocks first, then options
    sortedGroups.forEach(group => {
      group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
    })

    return { groups: sortedGroups, accountNumbers }
  }, [includedAccounts, allAccountPortfolios])

  // Build nominal exposure comparison data structure (same as above but using nominal_exposure_pct)
  const nominalComparisonData = useMemo(() => {
    if (!includedAccounts.length || allAccountPortfolios.some(q => q.isLoading)) {
      return { groups: [] as ComparisonGroup[], accountNumbers: [] as string[] }
    }

    const accountNumbers = includedAccounts.map(a => a.account_number)
    const groups: Record<string, ComparisonGroup> = {}

    // Process each account's positions
    allAccountPortfolios.forEach((result, idx) => {
      if (!result.data) return
      const acctNum = includedAccounts[idx]?.account_number
      if (!acctNum) return

      const positions = result.data.open_positions.filter(
        p => p.row_type === 'Data' && p.symbol && p.asset_category !== 'Futures'
      )

      for (const pos of positions) {
        const isOptions = pos.asset_category === 'Equity and Index Options'
        let underlying = pos.symbol || ''
        if (isOptions) {
          underlying = getUnderlying(pos.symbol || '', pos.asset_category) || pos.symbol || ''
        }

        // Initialize group if needed
        if (!groups[underlying]) {
          groups[underlying] = {
            underlying,
            hasOptions: false,
            accountPcts: {},
            positions: [],
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            groups[underlying].accountPcts[an] = null
          })
        }

        const group = groups[underlying]
        if (isOptions) group.hasOptions = true

        // Add nominal exposure percentage for this account
        const nominalPct = pos.nominal_exposure_pct !== null ? Number(pos.nominal_exposure_pct) : null
        group.accountPcts[acctNum] = (group.accountPcts[acctNum] || 0) + (nominalPct || 0)

        // Find or create position detail
        let posDetail = group.positions.find(p => p.symbol === pos.symbol)
        if (!posDetail) {
          posDetail = {
            symbol: pos.symbol || '',
            isOption: isOptions,
            accountPcts: {},
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            posDetail!.accountPcts[an] = null
          })
          group.positions.push(posDetail)
        }
        posDetail.accountPcts[acctNum] = nominalPct
      }
    })

    // Sort groups by total value and positions within groups
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const aTotal = Object.values(a.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      const bTotal = Object.values(b.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      return bTotal - aTotal
    })

    // Sort positions within each group: stocks first, then options
    sortedGroups.forEach(group => {
      group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
    })

    return { groups: sortedGroups, accountNumbers }
  }, [includedAccounts, allAccountPortfolios])

  // Build NC exposure comparison data structure (same as above but using nc_exposure_pct)
  const ncComparisonData = useMemo(() => {
    if (!includedAccounts.length || allAccountPortfolios.some(q => q.isLoading)) {
      return { groups: [] as ComparisonGroup[], accountNumbers: [] as string[] }
    }

    const accountNumbers = includedAccounts.map(a => a.account_number)
    const groups: Record<string, ComparisonGroup> = {}

    // Process each account's positions
    allAccountPortfolios.forEach((result, idx) => {
      if (!result.data) return
      const acctNum = includedAccounts[idx]?.account_number
      if (!acctNum) return

      const positions = result.data.open_positions.filter(
        p => p.row_type === 'Data' && p.symbol && p.asset_category !== 'Futures'
      )

      for (const pos of positions) {
        const isOptions = pos.asset_category === 'Equity and Index Options'
        let underlying = pos.symbol || ''
        if (isOptions) {
          underlying = getUnderlying(pos.symbol || '', pos.asset_category) || pos.symbol || ''
        }

        // Initialize group if needed
        if (!groups[underlying]) {
          groups[underlying] = {
            underlying,
            hasOptions: false,
            accountPcts: {},
            positions: [],
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            groups[underlying].accountPcts[an] = null
          })
        }

        const group = groups[underlying]
        if (isOptions) group.hasOptions = true

        // Add NC exposure percentage for this account
        const ncPct = pos.nc_exposure_pct !== null ? Number(pos.nc_exposure_pct) : null
        group.accountPcts[acctNum] = (group.accountPcts[acctNum] || 0) + (ncPct || 0)

        // Find or create position detail
        let posDetail = group.positions.find(p => p.symbol === pos.symbol)
        if (!posDetail) {
          posDetail = {
            symbol: pos.symbol || '',
            isOption: isOptions,
            accountPcts: {},
          }
          // Initialize all account columns to null
          accountNumbers.forEach(an => {
            posDetail!.accountPcts[an] = null
          })
          group.positions.push(posDetail)
        }
        posDetail.accountPcts[acctNum] = ncPct
      }
    })

    // Sort groups by total value and positions within groups
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const aTotal = Object.values(a.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      const bTotal = Object.values(b.accountPcts).reduce<number>((sum, v) => sum + (v || 0), 0)
      return bTotal - aTotal
    })

    // Sort positions within each group: stocks first, then options
    sortedGroups.forEach(group => {
      group.positions.sort((a, b) => (a.isOption ? 1 : 0) - (b.isOption ? 1 : 0))
    })

    return { groups: sortedGroups, accountNumbers }
  }, [includedAccounts, allAccountPortfolios])

  // Calculate weighted average for comparison view
  const calculateWeightedAvg = useMemo(() => {
    return (accountPcts: Record<string, number | null>): number | null => {
      let weightedSum = 0
      let totalWeight = 0
      for (const [acctNum, pct] of Object.entries(accountPcts)) {
        if (pct !== null && accountCalculatedTotals[acctNum]) {
          weightedSum += pct * accountCalculatedTotals[acctNum]
          totalWeight += accountCalculatedTotals[acctNum]
        }
      }
      return totalWeight > 0 ? weightedSum / totalWeight : null
    }
  }, [accountCalculatedTotals])

  // Calculate column totals for comparison view
  const comparisonTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    comparisonData.accountNumbers.forEach(an => {
      totals[an] = comparisonData.groups.reduce(
        (sum, g) => sum + (g.accountPcts[an] || 0),
        0
      )
    })
    return totals
  }, [comparisonData])

  // Sort account columns for comparison view
  const sortedComparisonAccounts = useMemo(() => {
    const accounts = [...comparisonData.accountNumbers]
    if (comparisonAccountSort === 'value') {
      accounts.sort((a, b) => (accountCalculatedTotals[b] || 0) - (accountCalculatedTotals[a] || 0))
    }
    return accounts
  }, [comparisonData.accountNumbers, comparisonAccountSort, accountCalculatedTotals])

  // Sort groups for comparison view
  const sortedComparisonGroups = useMemo(() => {
    const groups = [...comparisonData.groups]
    const { column, direction } = comparisonRowSort
    const multiplier = direction === 'desc' ? 1 : -1

    groups.sort((a, b) => {
      let aVal: number | null, bVal: number | null
      if (column === 'wtdAvg') {
        aVal = calculateWeightedAvg(a.accountPcts)
        bVal = calculateWeightedAvg(b.accountPcts)
      } else {
        aVal = a.accountPcts[column]
        bVal = b.accountPcts[column]
      }
      return multiplier * ((bVal || 0) - (aVal || 0))
    })
    return groups
  }, [comparisonData.groups, comparisonRowSort, calculateWeightedAvg])

  // Delta comparison: calculate column totals
  const deltaComparisonTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    deltaComparisonData.accountNumbers.forEach(an => {
      totals[an] = deltaComparisonData.groups.reduce(
        (sum, g) => sum + (g.accountPcts[an] || 0),
        0
      )
    })
    return totals
  }, [deltaComparisonData])

  // Delta comparison: sort account columns
  const sortedDeltaComparisonAccounts = useMemo(() => {
    const accounts = [...deltaComparisonData.accountNumbers]
    if (deltaComparisonAccountSort === 'value') {
      accounts.sort((a, b) => (accountCalculatedTotals[b] || 0) - (accountCalculatedTotals[a] || 0))
    }
    return accounts
  }, [deltaComparisonData.accountNumbers, deltaComparisonAccountSort, accountCalculatedTotals])

  // Delta comparison: sort groups
  const sortedDeltaComparisonGroups = useMemo(() => {
    const groups = [...deltaComparisonData.groups]
    const { column, direction } = deltaComparisonRowSort
    const multiplier = direction === 'desc' ? 1 : -1

    groups.sort((a, b) => {
      let aVal: number | null, bVal: number | null
      if (column === 'wtdAvg') {
        aVal = calculateWeightedAvg(a.accountPcts)
        bVal = calculateWeightedAvg(b.accountPcts)
      } else {
        aVal = a.accountPcts[column]
        bVal = b.accountPcts[column]
      }
      return multiplier * ((bVal || 0) - (aVal || 0))
    })
    return groups
  }, [deltaComparisonData.groups, deltaComparisonRowSort, calculateWeightedAvg])

  // Nominal comparison: calculate column totals
  const nominalComparisonTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    nominalComparisonData.accountNumbers.forEach(an => {
      totals[an] = nominalComparisonData.groups.reduce(
        (sum, g) => sum + (g.accountPcts[an] || 0),
        0
      )
    })
    return totals
  }, [nominalComparisonData])

  // Nominal comparison: sort account columns
  const sortedNominalComparisonAccounts = useMemo(() => {
    const accounts = [...nominalComparisonData.accountNumbers]
    if (nominalComparisonAccountSort === 'value') {
      accounts.sort((a, b) => (accountCalculatedTotals[b] || 0) - (accountCalculatedTotals[a] || 0))
    }
    return accounts
  }, [nominalComparisonData.accountNumbers, nominalComparisonAccountSort, accountCalculatedTotals])

  // Nominal comparison: sort groups
  const sortedNominalComparisonGroups = useMemo(() => {
    const groups = [...nominalComparisonData.groups]
    const { column, direction } = nominalComparisonRowSort
    const multiplier = direction === 'desc' ? 1 : -1

    groups.sort((a, b) => {
      let aVal: number | null, bVal: number | null
      if (column === 'wtdAvg') {
        aVal = calculateWeightedAvg(a.accountPcts)
        bVal = calculateWeightedAvg(b.accountPcts)
      } else {
        aVal = a.accountPcts[column]
        bVal = b.accountPcts[column]
      }
      return multiplier * ((bVal || 0) - (aVal || 0))
    })
    return groups
  }, [nominalComparisonData.groups, nominalComparisonRowSort, calculateWeightedAvg])

  // NC comparison: calculate account totals
  const ncComparisonAccountTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    ncComparisonData.accountNumbers.forEach(an => {
      totals[an] = ncComparisonData.groups.reduce(
        (sum, g) => sum + (g.accountPcts[an] || 0),
        0
      )
    })
    return totals
  }, [ncComparisonData])

  // NC comparison: sort account columns
  const sortedNcComparisonAccounts = useMemo(() => {
    const accounts = [...ncComparisonData.accountNumbers]
    if (ncComparisonAccountSort === 'value') {
      accounts.sort((a, b) => (accountCalculatedTotals[b] || 0) - (accountCalculatedTotals[a] || 0))
    }
    return accounts
  }, [ncComparisonData.accountNumbers, ncComparisonAccountSort, accountCalculatedTotals])

  // NC comparison: sort groups
  const sortedNcComparisonGroups = useMemo(() => {
    const groups = [...ncComparisonData.groups]
    const { column, direction } = ncComparisonRowSort
    const multiplier = direction === 'desc' ? 1 : -1

    groups.sort((a, b) => {
      let aVal: number | null, bVal: number | null
      if (column === 'wtdAvg') {
        aVal = calculateWeightedAvg(a.accountPcts)
        bVal = calculateWeightedAvg(b.accountPcts)
      } else {
        aVal = a.accountPcts[column]
        bVal = b.accountPcts[column]
      }
      return multiplier * ((bVal || 0) - (aVal || 0))
    })
    return groups
  }, [ncComparisonData.groups, ncComparisonRowSort, calculateWeightedAvg])

  // Helper to check if a value should be highlighted (tiered: 0.5-1% light, >1% heavy)
  const getHighlight = (
    value: number | null,
    weightedAvg: number | null
  ): 'high-light' | 'high-heavy' | 'low-light' | 'low-heavy' | null => {
    if (weightedAvg === null) return null
    // Treat null as 0 for comparison purposes
    const val = value ?? 0
    const diff = val - weightedAvg
    const lightThreshold = 0.005 // 0.5%
    const heavyThreshold = 0.01  // 1%

    if (diff > heavyThreshold) return 'high-heavy'
    if (diff > lightThreshold) return 'high-light'
    if (diff < -heavyThreshold) return 'low-heavy'
    if (diff < -lightThreshold) return 'low-light'
    return null
  }

  // Helper for Total/Subsector Total rows with higher thresholds (1%/2%)
  const getHighlightTotal = (
    value: number | null,
    weightedAvg: number | null
  ): 'high-light' | 'high-heavy' | 'low-light' | 'low-heavy' | null => {
    if (weightedAvg === null) return null
    const val = value ?? 0
    const diff = val - weightedAvg
    const lightThreshold = 0.01 // 1%
    const heavyThreshold = 0.02 // 2%

    if (diff > heavyThreshold) return 'high-heavy'
    if (diff > lightThreshold) return 'high-light'
    if (diff < -heavyThreshold) return 'low-heavy'
    if (diff < -lightThreshold) return 'low-light'
    return null
  }

  // Toggle comparison row sort
  const toggleComparisonRowSort = (column: string) => {
    setComparisonRowSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }

  // Toggle delta comparison row sort
  const toggleDeltaComparisonRowSort = (column: string) => {
    setDeltaComparisonRowSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }

  // Toggle nominal comparison row sort
  const toggleNominalComparisonRowSort = (column: string) => {
    setNominalComparisonRowSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }

  // Toggle NC comparison row sort
  const toggleNcComparisonRowSort = (column: string) => {
    setNcComparisonRowSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
      }
      return { column, direction: 'desc' }
    })
  }

  if (uploadLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!upload) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Upload not found</p>
        <button onClick={() => navigate('/snapshots')} className="btn btn-primary mt-4">
          Back to Snapshots
        </button>
      </div>
    )
  }

  const aggregateTabs: { id: Tab; label: string }[] = [
    { id: 'agg-positions', label: 'Positions' },
    { id: 'agg-exposures', label: 'Exposures' },
    { id: 'agg-comparison', label: 'Position Comparison' },
    { id: 'agg-nominal-comparison', label: 'Nominal Exp Comparison' },
    { id: 'agg-delta-comparison', label: 'Δ Adj Exp Comparison' },
    { id: 'agg-nc-comparison', label: 'NC Exp Comparison' },
    { id: 'agg-nav', label: 'Net Asset Value' },
    { id: 'agg-cash', label: 'Cash Report' },
  ]

  const accountTabs: { id: Tab; label: string }[] = [
    { id: 'acct-positions', label: 'Positions' },
    { id: 'acct-exposures', label: 'Exposures' },
    { id: 'acct-nav', label: 'Net Asset Value' },
    { id: 'acct-cash', label: 'Cash Report' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/snapshots')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portfolio View</h1>
          <p className="text-sm text-gray-500">
            {upload.filename} - {new Date(upload.uploaded_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Account Selector - First */}
      {accounts && (
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Account:</label>
          <select
            value={selectedAccount}
            onChange={(e) =>
              handleAccountChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="input max-w-xs"
          >
            <option value="all">All Accounts (Aggregate)</option>
            {accounts.accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.account_name || account.account_number}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Summary Cards - Second */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`card flex items-center gap-4 ${summary.hasError ? 'border-2 border-amber-400' : ''}`}>
          <div className={`p-3 rounded-lg ${summary.hasError ? 'bg-amber-100' : 'bg-primary-100'}`}>
            <DollarSign className={`h-6 w-6 ${summary.hasError ? 'text-amber-600' : 'text-primary-600'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Total Value</p>
              {summary.hasError && (
                <span title="Total doesn't match Securities + Cash"><AlertTriangle className="h-4 w-4 text-amber-500" /></span>
              )}
            </div>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(summary.totalValue)}
            </p>
            {calculatedTotalUsd !== null && (
              <p className="text-xs text-gray-400">
                Calculated: {formatCurrency(calculatedTotalUsd)}
              </p>
            )}
            {summary.hasError && (
              <p className="text-xs text-amber-600">
                Expected: {formatCurrency(summary.calculatedTotal)} (Sec + Cash)
              </p>
            )}
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <TrendingUp className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Securities</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(summary.securities)}{calculatedTotalUsd ? ` (${((summary.securities / calculatedTotalUsd) * 100).toFixed(1)}%)` : ''}
            </p>
            {calculatedTotalUsd !== null && (
              <p className="text-xs text-gray-400">
                Positions (Eq+Opt): {formatCurrency(calculatedTotalUsd - summary.cash)} ({(((calculatedTotalUsd - summary.cash) / calculatedTotalUsd) * 100).toFixed(1)}%)
              </p>
            )}
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-yellow-100 rounded-lg">
            <Briefcase className="h-6 w-6 text-yellow-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Cash</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(summary.cash)}{calculatedTotalUsd ? ` (${((summary.cash / calculatedTotalUsd) * 100).toFixed(1)}%)` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* FX Rates Link */}
      {fxRates && fxRates.length > 0 && (
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>Using FX rates from {new Date(fxRates[0].rate_date).toLocaleDateString()}</span>
          <a href="/fx-rates" className="text-primary-600 hover:underline">View/Manage Rates</a>
        </div>
      )}

      {/* Tabs - Two Groups */}
      <div className="border-b border-gray-200">
        <nav className="flex items-start">
          {/* Aggregate Group */}
          <div className="flex flex-col items-center">
            <span className={`text-xs font-semibold uppercase tracking-wide mb-1 border-b ${isAggregateDisabled ? 'text-gray-300 border-gray-200' : 'text-gray-500 border-gray-400'}`}>
              Aggregate
            </span>
            <div className="flex gap-1">
              {aggregateTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => !isAggregateDisabled && setActiveTab(tab.id)}
                  disabled={isAggregateDisabled}
                  className={`py-2 px-3 rounded-t-lg font-medium text-sm transition-colors ${
                    isAggregateDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  title={isAggregateDisabled ? 'Select "All Accounts" to view aggregate' : undefined}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="w-px h-12 bg-gray-300 mx-4 self-center" />

          {/* Account Group */}
          <div className="flex flex-col items-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 border-b border-gray-400">
              Account
            </span>
            <div className="flex gap-1">
              {accountTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-3 rounded-t-lg font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        {/* Excluded Accounts Management - shown for all aggregate tabs */}
        {activeTab.startsWith('agg-') && aggregate && accounts && (
          <div className="border rounded-lg mb-6">
            <button
              onClick={() => setShowExcludedAccounts(!showExcludedAccounts)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <div className="flex items-center gap-2">
                {showExcludedAccounts ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>Excluded Accounts ({aggregate.excluded_accounts.length})</span>
              </div>
              <span className="text-xs text-gray-500">
                {aggregate.included_accounts.length} accounts in aggregate
              </span>
            </button>
            {showExcludedAccounts && (
              <div className="px-4 py-3 border-t bg-gray-50">
                <div className="space-y-3">
                  {aggregate.excluded_accounts.length > 0 ? (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Currently Excluded:</p>
                      <div className="flex flex-wrap gap-2">
                        {aggregate.excluded_accounts.map(accountId => {
                          const account = accounts.accounts.find(a => a.id === accountId)
                          return (
                            <span
                              key={accountId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-sm rounded"
                            >
                              {account?.account_number || `Account ${accountId}`}
                              <button
                                onClick={() => toggleExclusionMutation.mutate(accountId)}
                                className="hover:bg-red-200 rounded p-0.5"
                                title="Include in aggregate"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No accounts excluded</p>
                  )}
                  {aggregate.included_accounts.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Click to Exclude:</p>
                      <div className="flex flex-wrap gap-2">
                        {aggregate.included_accounts.map(accountId => {
                          const account = accounts.accounts.find(a => a.id === accountId)
                          return (
                            <button
                              key={accountId}
                              onClick={() => toggleExclusionMutation.mutate(accountId)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200"
                              title="Exclude from aggregate"
                            >
                              <Plus className="h-3 w-3" />
                              {account?.account_number || `Account ${accountId}`}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'agg-positions' && (
          aggregateLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : aggregate ? (
            (() => {
              const positionsWithUsd = aggregate.positions.map(pos => ({
                ...pos,
                value_usd: convertToUsd(pos.total_value, pos.currency, fxRateMap)
              }))
              const equityOptions = positionsWithUsd.filter(p => p.asset_category !== 'Futures')
              const futures = positionsWithUsd.filter(p => p.asset_category === 'Futures')

              const eqTotals = equityOptions.reduce((acc, p) => ({
                value: acc.value + (Number(p.total_value) || 0),
                valueUsd: acc.valueUsd + (p.value_usd || 0),
                nominal: acc.nominal + (Number(p.total_nominal_exposure) || 0),
                deltaAdj: acc.deltaAdj + (Number(p.total_delta_adjusted_exposure) || 0),
              }), { value: 0, valueUsd: 0, nominal: 0, deltaAdj: 0 })

              const futTotals = futures.reduce((acc, p) => ({
                value: acc.value + (Number(p.total_value) || 0),
                valueUsd: acc.valueUsd + (p.value_usd || 0),
                nominal: acc.nominal + (Number(p.total_nominal_exposure) || 0),
                deltaAdj: acc.deltaAdj + (Number(p.total_delta_adjusted_exposure) || 0),
              }), { value: 0, valueUsd: 0, nominal: 0, deltaAdj: 0 })

              return (
                <div className="space-y-6">
                  {/* Equity & Options Positions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Equity & Options</h3>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <SortableHeader<AggregatedPositionWithUsd> label="Symbol" sortKey="symbol" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-left" tableKey="agg-positions" defaultWidth={180} />
                            <ResizableHeader tableKey="agg-positions" columnKey="underlying" defaultWidth={100} className="text-left">Underlying</ResizableHeader>
                            <SortableHeader<AggregatedPositionWithUsd> label="Category" sortKey="asset_category" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-left" tableKey="agg-positions" defaultWidth={160} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Quantity" sortKey="total_quantity" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={90} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Price" sortKey="close_price" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={100} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Value" sortKey="total_value" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={110} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Currency" sortKey="currency" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-left" tableKey="agg-positions" defaultWidth={80} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Value (USD)" sortKey="value_usd" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={110} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Nominal Exp" sortKey="total_nominal_exposure" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={110} />
                            <SortableHeader<AggregatedPositionWithUsd> label="Delta-Adj Exp" sortKey="total_delta_adjusted_exposure" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={110} />
                            <SortableHeader<AggregatedPositionWithUsd> label="IV" sortKey="implied_volatility" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-positions" defaultWidth={70} />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortData(equityOptions, aggPositionsSort).map((pos, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-900">{pos.symbol || '-'}</p>
                                  <p className="text-xs text-gray-500 truncate max-w-[200px]">{pos.description}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{getUnderlying(pos.symbol, pos.asset_category) || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{pos.asset_category}</td>
                              <td className="px-4 py-3 text-sm text-right">{formatQuantity(pos.total_quantity)}</td>
                              <td className="px-4 py-3 text-sm text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {formatCurrency(pos.close_price)}
                                  {pos.close_price_mismatch && (
                                    <span title="Price differs across accounts"><AlertTriangle className="h-4 w-4 text-amber-500" /></span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(pos.total_value)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{pos.currency || '-'}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.value_usd)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.total_nominal_exposure)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.total_delta_adjusted_exposure)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{pos.implied_volatility != null ? `${(pos.implied_volatility * 100).toFixed(1)}%` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-semibold">
                          <tr>
                            <td className="px-4 py-3 text-gray-900">Total Equity & Options</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right">-</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right">{formatCurrency(calculatedTotalUsd !== null ? calculatedTotalUsd - summary.cash : eqTotals.valueUsd)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.nominal)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.deltaAdj)}</td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Futures Positions */}
                  {futures.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Futures</h3>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <SortableHeader<AggregatedPositionWithUsd> label="Symbol" sortKey="symbol" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-left" tableKey="agg-futures" defaultWidth={180} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Quantity" sortKey="total_quantity" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={90} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Price" sortKey="close_price" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={100} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Value" sortKey="total_value" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={110} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Currency" sortKey="currency" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-left" tableKey="agg-futures" defaultWidth={80} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Value (USD)" sortKey="value_usd" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={110} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Nominal Exp" sortKey="total_nominal_exposure" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={110} />
                              <SortableHeader<AggregatedPositionWithUsd> label="Delta-Adj Exp" sortKey="total_delta_adjusted_exposure" sortConfig={aggPositionsSort} onSort={setAggPositionsSort} className="text-right" tableKey="agg-futures" defaultWidth={110} />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {sortData(futures, aggPositionsSort).map((pos, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="font-medium text-gray-900">{pos.symbol || '-'}</p>
                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{pos.description}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-right">{formatQuantity(pos.total_quantity)}</td>
                                <td className="px-4 py-3 text-sm text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    {formatCurrency(pos.close_price)}
                                    {pos.close_price_mismatch && (
                                      <span title="Price differs across accounts"><AlertTriangle className="h-4 w-4 text-amber-500" /></span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(pos.total_value)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{pos.currency || '-'}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.value_usd)}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.total_nominal_exposure)}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.total_delta_adjusted_exposure)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 font-semibold">
                            <tr>
                              <td className="px-4 py-3 text-gray-900">Total Futures</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right">-</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right">{formatCurrency(futTotals.valueUsd)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.nominal)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.deltaAdj)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )
            })()
          ) : (
            <p className="text-gray-500 text-center py-8">No aggregate data available</p>
          )
        )}

        {activeTab === 'agg-exposures' && (
          aggregateLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : aggregate ? (
            (() => {
              const { equityAndOptions, futures } = groupAggregatePositionsByUnderlying(aggregate.positions, fxRateMap, accountIdToNumber, optionUnderlyingMap)
              // Calculate section totals
              const eqTotals = equityAndOptions.reduce((acc, g) => ({
                value: acc.value + (g.totalValueUsd || 0),
                nominal: acc.nominal + (g.totalNominalExposure || 0),
                deltaAdj: acc.deltaAdj + (g.totalDeltaAdjustedExposure || 0),
                ncExp: acc.ncExp + (g.totalNcExposure || 0),
                posPct: acc.posPct + (g.totalPositionPct || 0),
                deltaExpPct: acc.deltaExpPct + (g.totalDeltaExposurePct || 0),
                ncExpPct: acc.ncExpPct + (g.totalNcExposurePct || 0),
              }), { value: 0, nominal: 0, deltaAdj: 0, ncExp: 0, posPct: 0, deltaExpPct: 0, ncExpPct: 0 })
              const futTotals = futures.reduce((acc, g) => ({
                value: acc.value + (g.totalValueUsd || 0),
                nominal: acc.nominal + (g.totalNominalExposure || 0),
                deltaAdj: acc.deltaAdj + (g.totalDeltaAdjustedExposure || 0),
                ncExp: acc.ncExp + (g.totalNcExposure || 0),
                posPct: acc.posPct + (g.totalPositionPct || 0),
                deltaExpPct: acc.deltaExpPct + (g.totalDeltaExposurePct || 0),
                ncExpPct: acc.ncExpPct + (g.totalNcExposurePct || 0),
              }), { value: 0, nominal: 0, deltaAdj: 0, ncExp: 0, posPct: 0, deltaExpPct: 0, ncExpPct: 0 })

              // Collect all account numbers for the totals
              const allEqAccounts = new Set<string>()
              equityAndOptions.forEach(g => g.accountNumbers.forEach(a => allEqAccounts.add(a)))
              const allFutAccounts = new Set<string>()
              futures.forEach(g => g.accountNumbers.forEach(a => allFutAccounts.add(a)))

              // Group by currency and type
              const byCurrency = groupAggregatePositionsByCurrency(aggregate.positions, fxRateMap, accountIdToNumber)
              const byType = groupAggregatePositionsByType(aggregate.positions, fxRateMap, accountIdToNumber)

              return (
                <div className="space-y-4">
                  {/* By Underlying Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('agg-byUnderlying')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('agg-byUnderlying') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Underlying
                      </h3>
                      <span className="text-sm text-gray-500">{equityAndOptions.length + futures.length} groups</span>
                    </button>
                    {expandedSections.has('agg-byUnderlying') && (
                    <div className="p-4 space-y-6">
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <SortableHeader<GroupedExposure> label="Underlying" sortKey="underlying" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-left" tableKey="agg-exposures" defaultWidth={120} />
                            <ResizableHeader tableKey="agg-exposures" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                            <ResizableHeader tableKey="agg-exposures" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                            <SortableHeader<GroupedExposure> label="Quantity" sortKey="totalQuantity" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={80} />
                            <ResizableHeader tableKey="agg-exposures" columnKey="price" defaultWidth={90} className="text-right">Price</ResizableHeader>
                            <ResizableHeader tableKey="agg-exposures" columnKey="value" defaultWidth={100} className="text-right">Value</ResizableHeader>
                            <ResizableHeader tableKey="agg-exposures" columnKey="ccy" defaultWidth={50} className="text-left">Ccy</ResizableHeader>
                            <SortableHeader<GroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={70} />
                            <SortableHeader<GroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Nominal Exp %" sortKey="totalNominalExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={95} />
                            <SortableHeader<GroupedExposure> label="Δ Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Δ Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={95} />
                            <SortableHeader<GroupedExposure> label="NC Exp" sortKey="totalNcExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="NC Exp %" sortKey="totalNcExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="agg-exposures" defaultWidth={85} />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortData(equityAndOptions, exposuresSort).map((group) => {
                            const key = group.underlying
                            const isExpanded = expandedExposures.has(key)
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            return (
                              <React.Fragment key={key}>
                                <tr
                                  className="hover:bg-gray-50 cursor-pointer"
                                  onClick={() => toggleExpanded(key)}
                                >
                                  <td className="px-4 py-3 text-sm">
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{typeLabel}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-gray-600"></td>
                                  <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalNominalExposure || 0) / calculatedTotalUsd : null)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                  <td className={`px-4 py-3 text-sm text-right ${group.hasNcExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {group.hasNcExposureError ? 'Unlimited Loss' : formatCurrency(group.totalNcExposure)}
                                  </td>
                                  <td className={`px-4 py-3 text-sm text-right ${group.hasNcExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {group.hasNcExposureError ? 'Potential' : formatPercent(calculatedTotalUsd ? (group.totalNcExposure || 0) / calculatedTotalUsd : null)}
                                  </td>
                                </tr>
                                {isExpanded && group.positions.map((pos, idx) => (
                                  <tr key={`${key}-${idx}`} className="bg-gray-50">
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.price)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.nominalExposurePct)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                    <td className={`px-4 py-2 text-sm text-right ${pos.ncExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {pos.ncExposureError ? 'Unlimited Loss' : formatCurrency(pos.ncExposure)}
                                    </td>
                                    <td className={`px-4 py-2 text-sm text-right ${pos.ncExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {pos.ncExposureError ? 'Potential' : formatPercent(pos.ncExposurePct)}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-gray-100 font-semibold">
                          <tr>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-gray-900">Total Equity & Options</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(allEqAccounts)}</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(eqTotals.value)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.value / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.nominal)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.nominal / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.deltaAdj)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.deltaAdj / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.ncExp)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.ncExp / calculatedTotalUsd : null)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                  {/* Futures */}
                  {futures.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Futures</h3>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                              <SortableHeader<FuturesGroupedExposure> label="Underlying" sortKey="underlying" sortConfig={futuresSort} onSort={setFuturesSort} className="text-left" tableKey="agg-futures-exp" defaultWidth={120} />
                              <ResizableHeader tableKey="agg-futures-exp" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                              <SortableHeader<FuturesGroupedExposure> label="Quantity" sortKey="totalQuantity" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={80} />
                              <ResizableHeader tableKey="agg-futures-exp" columnKey="price" defaultWidth={90} className="text-right">Price</ResizableHeader>
                              <ResizableHeader tableKey="agg-futures-exp" columnKey="value" defaultWidth={100} className="text-right">Value</ResizableHeader>
                              <ResizableHeader tableKey="agg-futures-exp" columnKey="ccy" defaultWidth={50} className="text-left">Ccy</ResizableHeader>
                              <SortableHeader<FuturesGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={100} />
                              <SortableHeader<FuturesGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={100} />
                              <SortableHeader<FuturesGroupedExposure> label="Nominal Exp %" sortKey="totalNominalExposurePct" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={95} />
                              <SortableHeader<FuturesGroupedExposure> label="Δ Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={100} />
                              <SortableHeader<FuturesGroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={70} />
                              <SortableHeader<FuturesGroupedExposure> label="Δ Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="agg-futures-exp" defaultWidth={95} />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {sortData(futures, futuresSort).map((group) => {
                              const key = `futures-${group.underlying}`
                              const isExpanded = expandedExposures.has(key)
                              return (
                                <React.Fragment key={key}>
                                  <tr
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => toggleExpanded(key)}
                                  >
                                    <td className="px-4 py-3 text-sm">
                                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-gray-600"></td>
                                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalNominalExposure || 0) / calculatedTotalUsd : null)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                  </tr>
                                  {isExpanded && group.positions.map((pos, idx) => (
                                    <tr key={`${key}-${idx}`} className="bg-gray-50">
                                      <td className="px-4 py-2"></td>
                                      <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.price)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.nominalExposurePct)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                          <tfoot className="bg-gray-100 font-semibold">
                            <tr>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-gray-900">Total Futures</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(allFutAccounts)}</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(futTotals.value)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.nominal)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? futTotals.nominal / calculatedTotalUsd : null)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.deltaAdj)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? futTotals.value / calculatedTotalUsd : null)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? futTotals.deltaAdj / calculatedTotalUsd : null)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                    </div>
                    )}
                  </div>

                  {/* By Currency Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('agg-byCurrency')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('agg-byCurrency') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Currency (Equity & Options only)
                      </h3>
                      <span className="text-sm text-gray-500">{byCurrency.length} currencies</span>
                    </button>
                    {expandedSections.has('agg-byCurrency') && (
                      <div className="p-4">
                        <p className="text-sm text-amber-600 mb-3 bg-amber-50 p-2 rounded">
                          Note: If FX rates have moved since the report was generated, these totals may not match the overall totals.
                        </p>
                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                                <SortableHeader<CurrencyGroupedExposure> label="Currency" sortKey="currency" sortConfig={currencySort} onSort={setCurrencySort} className="text-left" tableKey="agg-by-currency" defaultWidth={100} />
                                <ResizableHeader tableKey="agg-by-currency" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                                <ResizableHeader tableKey="agg-by-currency" columnKey="positions" defaultWidth={80} className="text-right">Positions</ResizableHeader>
                                <SortableHeader<CurrencyGroupedExposure> label="Value" sortKey="totalValue" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={70} />
                                <SortableHeader<CurrencyGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={100} />
                                <SortableHeader<CurrencyGroupedExposure> label="Delta-Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Delta-Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="agg-by-currency" defaultWidth={105} />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {sortData(byCurrency, currencySort).map((group) => {
                                const key = `currency-${group.currency}`
                                const isExpanded = expandedExposures.has(key)
                                return (
                                  <React.Fragment key={group.currency}>
                                    <tr
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => toggleExpanded(key)}
                                    >
                                      <td className="px-4 py-3 text-sm">
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </td>
                                      <td className="px-4 py-3 font-medium text-gray-900">{group.currency}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">{group.positionCount}</td>
                                      <td className="px-4 py-3 text-sm text-right">{formatValue(group.totalValue)}</td>
                                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                    </tr>
                                    {isExpanded && group.positions.map((pos, idx) => (
                                      <tr key={`${key}-${idx}`} className="bg-gray-50">
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-sm text-gray-600 pl-4">{pos.symbol}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                            <tfoot className="bg-gray-100 font-semibold">
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{byCurrency.reduce((sum, g) => sum + g.positionCount, 0)}</td>
                                <td className="px-4 py-3 text-right">-</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byCurrency.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0) / calculatedTotalUsd : null)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalNominalExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byCurrency.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0) / calculatedTotalUsd : null)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* By Type Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('agg-byType')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('agg-byType') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Type
                      </h3>
                      <span className="text-sm text-gray-500">{byType.length} types</span>
                    </button>
                    {expandedSections.has('agg-byType') && (
                      <div className="p-4">
                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                                <SortableHeader<TypeGroupedExposure> label="Asset Type" sortKey="assetType" sortConfig={typeSort} onSort={setTypeSort} className="text-left" tableKey="agg-by-type" defaultWidth={160} />
                                <ResizableHeader tableKey="agg-by-type" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                                <ResizableHeader tableKey="agg-by-type" columnKey="positions" defaultWidth={80} className="text-right">Positions</ResizableHeader>
                                <ResizableHeader tableKey="agg-by-type" columnKey="currencies" defaultWidth={90} className="text-left">Currencies</ResizableHeader>
                                <SortableHeader<TypeGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="agg-by-type" defaultWidth={110} />
                                <SortableHeader<TypeGroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="agg-by-type" defaultWidth={70} />
                                <SortableHeader<TypeGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="agg-by-type" defaultWidth={100} />
                                <SortableHeader<TypeGroupedExposure> label="Delta-Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="agg-by-type" defaultWidth={110} />
                                <SortableHeader<TypeGroupedExposure> label="Delta-Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="agg-by-type" defaultWidth={105} />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {sortData(byType, typeSort).map((group) => {
                                const key = `type-${group.assetType}`
                                const isExpanded = expandedExposures.has(key)
                                return (
                                  <React.Fragment key={group.assetType}>
                                    <tr
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => toggleExpanded(key)}
                                    >
                                      <td className="px-4 py-3 text-sm">
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </td>
                                      <td className="px-4 py-3 font-medium text-gray-900">{group.assetType}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">{group.positionCount}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{group.currencies.size > 1 ? `${group.currencies.size} currencies` : Array.from(group.currencies)[0] || '-'}</td>
                                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                    </tr>
                                    {isExpanded && group.positions.map((pos, idx) => (
                                      <tr key={`${key}-${idx}`} className="bg-gray-50">
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-sm text-gray-600 pl-4">{pos.symbol}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                            <tfoot className="bg-gray-100 font-semibold">
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{byType.reduce((sum, g) => sum + g.positionCount, 0)}</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byType.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0) / calculatedTotalUsd : null)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalNominalExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byType.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0) / calculatedTotalUsd : null)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()
          ) : (
            <p className="text-gray-500 text-center py-8">No exposure data available</p>
          )
        )}

        {activeTab === 'agg-comparison' && (
          allAccountPortfolios.some(q => q.isLoading) ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : comparisonData.groups.length > 0 ? (
            <div className="space-y-4">
              {/* Excluded Accounts Toggle */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Comparing {includedAccounts.length} accounts
                  {accounts?.accounts.some(a => a.exclude_from_aggregate) && (
                    <button
                      onClick={() => setShowExcludedAccounts(!showExcludedAccounts)}
                      className="ml-2 text-primary-600 hover:text-primary-700"
                    >
                      {showExcludedAccounts ? 'Hide' : 'Show'} excluded accounts
                    </button>
                  )}
                </div>
              </div>

              {/* Excluded Accounts Panel */}
              {showExcludedAccounts && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Account Exclusion Settings</h4>
                  <div className="flex flex-wrap gap-2">
                    {accounts?.accounts.map(account => (
                      <button
                        key={account.id}
                        onClick={() => toggleExclusionMutation.mutate(account.id)}
                        disabled={toggleExclusionMutation.isPending}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          account.exclude_from_aggregate
                            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        }`}
                      >
                        {account.exclude_from_aggregate ? <Plus className="inline h-3 w-3 mr-1" /> : <X className="inline h-3 w-3 mr-1" />}
                        {account.account_number}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Click to include/exclude accounts from comparison. Excluded accounts are grayed out.
                  </p>
                </div>
              )}

              {/* Subsector Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Subsector:</label>
                <select
                  value={selectedSubsectorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'create') {
                      setShowCreateSubsectorModal(true)
                    } else {
                      setSelectedSubsectorId(val ? Number(val) : null)
                    }
                  }}
                  className="input py-1 px-2 text-sm w-48"
                >
                  <option value="">None</option>
                  {subsectorsData?.subsectors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                  <option value="create">+ Create new...</option>
                </select>
              </div>

              {/* Comparison Table */}
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                {/* Sort Controls */}
                <div className="flex items-center gap-4 mb-2 text-sm">
                  <span className="text-gray-500">Sort accounts by:</span>
                  <button
                    onClick={() => setComparisonAccountSort('number')}
                    className={`px-2 py-1 rounded ${comparisonAccountSort === 'number' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Account Number
                  </button>
                  <button
                    onClick={() => setComparisonAccountSort('value')}
                    className={`px-2 py-1 rounded ${comparisonAccountSort === 'value' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Portfolio Value
                  </button>
                </div>

                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    {/* Account Headers Row */}
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                      <ResizableHeader tableKey="agg-comparison" columnKey="underlying" defaultWidth={120} className="text-left">Underlying</ResizableHeader>
                      <ResizableHeader tableKey="agg-comparison" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                      {sortedComparisonAccounts.map(acctNum => (
                        <th
                          key={acctNum}
                          className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-100 ${
                            comparisonRowSort.column === acctNum ? 'text-primary-700 bg-primary-50' : 'text-gray-500'
                          }`}
                          onClick={() => toggleComparisonRowSort(acctNum)}
                          style={{ width: '90px', minWidth: '90px' }}
                        >
                          {acctNum}
                          {comparisonRowSort.column === acctNum && (
                            <span className="ml-1">{comparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                          )}
                        </th>
                      ))}
                      <th
                        className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-200 ${
                          comparisonRowSort.column === 'wtdAvg' ? 'text-primary-700 bg-primary-100' : 'text-gray-500 bg-gray-100'
                        }`}
                        onClick={() => toggleComparisonRowSort('wtdAvg')}
                        style={{ width: '90px', minWidth: '90px' }}
                      >
                        Wtd Avg
                        {comparisonRowSort.column === 'wtdAvg' && (
                          <span className="ml-1">{comparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }}>
                        Action
                      </th>
                      {selectedSubsector && (
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }} colSpan={2}>
                          {selectedSubsector.name}
                        </th>
                      )}
                    </tr>
                    {/* Calculated Total USD Info Row */}
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-1"></th>
                      <th className="px-4 py-1 text-left text-xs text-gray-400 font-normal">Portfolio Value</th>
                      <th className="px-4 py-1"></th>
                      {sortedComparisonAccounts.map(acctNum => (
                        <th key={acctNum} className="px-4 py-1 text-right text-xs text-gray-400 font-normal">
                          {formatCurrency(accountCalculatedTotals[acctNum])}
                        </th>
                      ))}
                      <th className="px-4 py-1 text-right text-xs text-gray-400 font-normal bg-gray-200">
                        {formatCurrency(Object.values(accountCalculatedTotals).reduce((a, b) => a + b, 0))}
                      </th>
                      <th className="px-4 py-1"></th>
                      {selectedSubsector && <th className="px-2 py-1" colSpan={2}></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedComparisonGroups.map((group) => {
                      const key = `comp-${group.underlying}`
                      const isExpanded = expandedExposures.has(key)
                      const hasStocks = group.positions.some(p => !p.isOption)
                      const hasOptions = group.positions.some(p => p.isOption)
                      const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                      const weightedAvg = calculateWeightedAvg(group.accountPcts)

                      return (
                        <React.Fragment key={key}>
                          {/* Group Row */}
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleExpanded(key)}
                          >
                            <td className="px-4 py-3 text-sm">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{typeLabel}</td>
                            {sortedComparisonAccounts.map(acctNum => {
                              const val = group.accountPcts[acctNum]
                              const highlight = getHighlight(val, weightedAvg)
                              return (
                                <td
                                  key={acctNum}
                                  className={`px-4 py-3 text-sm text-right font-medium ${
                                    highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                    highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                    highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                    highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                  }`}
                                >
                                  {val !== null ? formatPercent(val) : '-'}
                                </td>
                              )
                            })}
                            <td className="px-4 py-3 text-sm text-right font-medium bg-gray-50">
                              {formatPercent(weightedAvg)}
                            </td>
                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/buysell/${encodeURIComponent(group.underlying)}?uploadId=${uploadId}&returnTab=${activeTab}`)}
                                className="p-1 rounded hover:bg-primary-100 text-primary-600"
                                title="Buy/Sell"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </button>
                            </td>
                            {selectedSubsector && (
                              <td className="px-2 py-3 text-center" colSpan={2} onClick={(e) => e.stopPropagation()}>
                                {underlyingsInSubsector.has(group.underlying) ? (
                                  <button
                                    onClick={() => removeFromSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-red-100 text-red-500"
                                    title={`Remove from ${selectedSubsector.name}`}
                                    disabled={removeFromSubsectorMutation.isPending}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => addToSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-green-100 text-green-500"
                                    title={`Add to ${selectedSubsector.name}`}
                                    disabled={addToSubsectorMutation.isPending}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>

                          {/* Expanded Position Rows */}
                          {isExpanded && group.positions.map((pos, idx) => {
                            const posWeightedAvg = calculateWeightedAvg(pos.accountPcts)
                            return (
                              <tr key={`${key}-${idx}`} className="bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                {sortedComparisonAccounts.map(acctNum => {
                                  const val = pos.accountPcts[acctNum]
                                  const highlight = getHighlight(val, posWeightedAvg)
                                  return (
                                    <td
                                      key={acctNum}
                                      className={`px-4 py-2 text-sm text-right ${
                                        highlight === 'high-heavy' ? 'bg-green-100 text-green-600' :
                                        highlight === 'high-light' ? 'bg-green-50 text-green-500' :
                                        highlight === 'low-heavy' ? 'bg-red-100 text-red-600' :
                                        highlight === 'low-light' ? 'bg-red-50 text-red-500' : 'text-gray-500'
                                      }`}
                                    >
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right text-gray-500 bg-gray-100">
                                  {formatPercent(posWeightedAvg)}
                                </td>
                                <td className="px-4 py-2"></td>
                                {selectedSubsector && <td className="px-2 py-2" colSpan={2}></td>}
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    {(() => {
                      const totalWtdAvg = calculateWeightedAvg(comparisonTotals)
                      return (
                        <tr>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-900">Total</td>
                          <td className="px-4 py-3"></td>
                          {sortedComparisonAccounts.map(acctNum => {
                            const val = comparisonTotals[acctNum]
                            const highlight = getHighlightTotal(val, totalWtdAvg)
                            return (
                              <td key={acctNum} className={`px-4 py-3 text-right ${
                                highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                              }`}>
                                {formatPercent(val)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-3 text-right text-gray-900 bg-gray-200">
                            {formatPercent(totalWtdAvg)}
                          </td>
                          <td className="px-4 py-3"></td>
                          {selectedSubsector && <td className="px-2 py-3" colSpan={2}></td>}
                        </tr>
                      )
                    })()}
                  </tfoot>
                </table>
              </div>

              {/* Filtered Subsector Table */}
              {selectedSubsector && (() => {
                const filteredGroups = sortedComparisonGroups.filter(g => underlyingsInSubsector.has(g.underlying))
                if (filteredGroups.length === 0) return null

                // Calculate filtered totals
                const filteredTotals: Record<string, number> = {}
                sortedComparisonAccounts.forEach(acct => {
                  filteredTotals[acct] = filteredGroups.reduce((sum, g) => sum + (g.accountPcts[acct] || 0), 0)
                })

                return (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {selectedSubsector.name} ({filteredGroups.length} underlyings)
                    </h4>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-blue-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '120px' }}>Underlying</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '110px' }}>Type</th>
                            {sortedComparisonAccounts.map(acctNum => (
                              <th key={acctNum} className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" style={{ width: '90px' }}>
                                {acctNum}
                              </th>
                            ))}
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase bg-blue-100" style={{ width: '90px' }}>
                              Wtd Avg
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredGroups.map((group) => {
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            const weightedAvg = calculateWeightedAvg(group.accountPcts)
                            return (
                              <tr key={group.underlying} className="hover:bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 font-medium text-gray-900">{group.underlying}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{typeLabel}</td>
                                {sortedComparisonAccounts.map(acctNum => {
                                  const val = group.accountPcts[acctNum]
                                  const highlight = getHighlight(val, weightedAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-2 text-sm text-right font-medium ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                    }`}>
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right font-medium bg-blue-50">
                                  {formatPercent(weightedAvg)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-blue-100 font-semibold">
                          {(() => {
                            const subsectorWtdAvg = calculateWeightedAvg(filteredTotals)
                            return (
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                {sortedComparisonAccounts.map(acctNum => {
                                  const val = filteredTotals[acctNum]
                                  const highlight = getHighlightTotal(val, subsectorWtdAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-3 text-right ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                                    }`}>
                                      {formatPercent(val)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-3 text-right text-gray-900 bg-blue-200">
                                  {formatPercent(subsectorWtdAvg)}
                                </td>
                              </tr>
                            )
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No position data available for comparison</p>
          )
        )}

        {activeTab === 'agg-nominal-comparison' && (
          allAccountPortfolios.some(q => q.isLoading) ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : nominalComparisonData.groups.length > 0 ? (
            <div className="space-y-4">
              {/* Excluded Accounts Toggle */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Comparing {includedAccounts.length} accounts (Nominal Exp %)
                  {accounts?.accounts.some(a => a.exclude_from_aggregate) && (
                    <button
                      onClick={() => setShowExcludedAccounts(!showExcludedAccounts)}
                      className="ml-2 text-primary-600 hover:text-primary-700"
                    >
                      {showExcludedAccounts ? 'Hide' : 'Show'} excluded accounts
                    </button>
                  )}
                </div>
              </div>

              {/* Excluded Accounts Panel */}
              {showExcludedAccounts && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Account Exclusion Settings</h4>
                  <div className="flex flex-wrap gap-2">
                    {accounts?.accounts.map(account => (
                      <button
                        key={account.id}
                        onClick={() => toggleExclusionMutation.mutate(account.id)}
                        disabled={toggleExclusionMutation.isPending}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          account.exclude_from_aggregate
                            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        }`}
                      >
                        {account.exclude_from_aggregate ? <Plus className="inline h-3 w-3 mr-1" /> : <X className="inline h-3 w-3 mr-1" />}
                        {account.account_number}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Click to include/exclude accounts from comparison. Excluded accounts are grayed out.
                  </p>
                </div>
              )}

              {/* Subsector Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Subsector:</label>
                <select
                  value={selectedSubsectorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'create') {
                      setShowCreateSubsectorModal(true)
                    } else {
                      setSelectedSubsectorId(val ? Number(val) : null)
                    }
                  }}
                  className="input py-1 px-2 text-sm w-48"
                >
                  <option value="">None</option>
                  {subsectorsData?.subsectors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                  <option value="create">+ Create new...</option>
                </select>
              </div>

              {/* Nominal Comparison Table */}
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                {/* Sort Controls */}
                <div className="flex items-center gap-4 mb-2 text-sm">
                  <span className="text-gray-500">Sort accounts by:</span>
                  <button
                    onClick={() => setNominalComparisonAccountSort('number')}
                    className={`px-2 py-1 rounded ${nominalComparisonAccountSort === 'number' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Account Number
                  </button>
                  <button
                    onClick={() => setNominalComparisonAccountSort('value')}
                    className={`px-2 py-1 rounded ${nominalComparisonAccountSort === 'value' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Portfolio Value
                  </button>
                </div>

                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    {/* Account Headers Row */}
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                      <ResizableHeader tableKey="agg-nominal-comparison" columnKey="underlying" defaultWidth={120} className="text-left">Underlying</ResizableHeader>
                      <ResizableHeader tableKey="agg-nominal-comparison" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                      {sortedNominalComparisonAccounts.map(acctNum => (
                        <th
                          key={acctNum}
                          className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-100 ${
                            nominalComparisonRowSort.column === acctNum ? 'text-primary-700 bg-primary-50' : 'text-gray-500'
                          }`}
                          onClick={() => toggleNominalComparisonRowSort(acctNum)}
                          style={{ width: '90px', minWidth: '90px' }}
                        >
                          {acctNum}
                          {nominalComparisonRowSort.column === acctNum && (
                            <span className="ml-1">{nominalComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                          )}
                        </th>
                      ))}
                      <th
                        className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-200 ${
                          nominalComparisonRowSort.column === 'wtdAvg' ? 'text-primary-700 bg-primary-100' : 'text-gray-500 bg-gray-100'
                        }`}
                        onClick={() => toggleNominalComparisonRowSort('wtdAvg')}
                        style={{ width: '90px', minWidth: '90px' }}
                      >
                        Wtd Avg
                        {nominalComparisonRowSort.column === 'wtdAvg' && (
                          <span className="ml-1">{nominalComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }}>
                        Action
                      </th>
                      {selectedSubsector && (
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }} colSpan={2}>
                          {selectedSubsector.name}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedNominalComparisonGroups.map((group) => {
                      const key = group.underlying
                      const isExpanded = expandedExposures.has(`nominal-${key}`)
                      const hasStocks = group.positions.some(p => !p.isOption)
                      const hasOptions = group.positions.some(p => p.isOption)
                      const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                      const weightedAvg = calculateWeightedAvg(group.accountPcts)
                      return (
                        <React.Fragment key={key}>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleExpanded(`nominal-${key}`)}
                          >
                            <td className="px-4 py-2 text-sm">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </td>
                            <td className="px-4 py-2 font-medium text-gray-900">{group.underlying}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">{typeLabel}</td>
                            {sortedNominalComparisonAccounts.map(acctNum => {
                              const highlight = getHighlight(group.accountPcts[acctNum], weightedAvg)
                              return (
                                <td
                                  key={acctNum}
                                  className={`px-4 py-2 text-sm text-right ${
                                    highlight === 'high-heavy' ? 'bg-green-100 text-green-800' :
                                    highlight === 'high-light' ? 'bg-green-50 text-green-700' :
                                    highlight === 'low-heavy' ? 'bg-red-100 text-red-800' :
                                    highlight === 'low-light' ? 'bg-red-50 text-red-700' :
                                    'text-gray-900'
                                  }`}
                                >
                                  {formatPercent(group.accountPcts[acctNum])}
                                </td>
                              )
                            })}
                            <td className="px-4 py-2 text-sm text-right font-medium text-gray-900 bg-gray-100">
                              {formatPercent(weightedAvg)}
                            </td>
                            <td className="px-4 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/buysell/${encodeURIComponent(group.underlying)}?uploadId=${uploadId}&returnTab=${activeTab}`)}
                                className="p-1 rounded hover:bg-primary-100 text-primary-600"
                                title="Buy/Sell"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </button>
                            </td>
                            {selectedSubsector && (
                              <td className="px-2 py-2 text-center" colSpan={2} onClick={(e) => e.stopPropagation()}>
                                {underlyingsInSubsector.has(group.underlying) ? (
                                  <button
                                    onClick={() => removeFromSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-red-100 text-red-500"
                                    title={`Remove from ${selectedSubsector.name}`}
                                    disabled={removeFromSubsectorMutation.isPending}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => addToSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-green-100 text-green-500"
                                    title={`Add to ${selectedSubsector.name}`}
                                    disabled={addToSubsectorMutation.isPending}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                          {isExpanded && group.positions.map((pos, idx) => {
                            const posWeightedAvg = calculateWeightedAvg(pos.accountPcts)
                            return (
                              <tr key={`${key}-${idx}`} className="bg-gray-50">
                                <td className="px-4 py-1"></td>
                                <td className="px-4 py-1 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                <td className="px-4 py-1 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                {sortedNominalComparisonAccounts.map(acctNum => {
                                  const highlight = getHighlight(pos.accountPcts[acctNum], posWeightedAvg)
                                  return (
                                    <td
                                      key={acctNum}
                                      className={`px-4 py-1 text-sm text-right ${
                                        highlight === 'high-heavy' ? 'bg-green-100 text-green-800' :
                                        highlight === 'high-light' ? 'bg-green-50 text-green-700' :
                                        highlight === 'low-heavy' ? 'bg-red-100 text-red-800' :
                                        highlight === 'low-light' ? 'bg-red-50 text-red-700' :
                                        'text-gray-500'
                                      }`}
                                    >
                                      {formatPercent(pos.accountPcts[acctNum])}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-1 text-sm text-right text-gray-600 bg-gray-100">
                                  {formatPercent(posWeightedAvg)}
                                </td>
                                <td className="px-4 py-1"></td>
                                {selectedSubsector && <td className="px-2 py-1" colSpan={2}></td>}
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold sticky bottom-0">
                    {(() => {
                      const totalWtdAvg = calculateWeightedAvg(nominalComparisonTotals)
                      return (
                        <tr>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-900">Total</td>
                          <td className="px-4 py-3"></td>
                          {sortedNominalComparisonAccounts.map(acctNum => {
                            const val = nominalComparisonTotals[acctNum]
                            const highlight = getHighlightTotal(val, totalWtdAvg)
                            return (
                              <td key={acctNum} className={`px-4 py-3 text-right ${
                                highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                              }`}>
                                {formatPercent(val)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-3 text-right text-gray-900 bg-gray-200">
                            {formatPercent(totalWtdAvg)}
                          </td>
                          <td className="px-4 py-3"></td>
                          {selectedSubsector && <td className="px-2 py-3" colSpan={2}></td>}
                        </tr>
                      )
                    })()}
                  </tfoot>
                </table>
              </div>

              {/* Filtered Subsector Table */}
              {selectedSubsector && (() => {
                const filteredGroups = sortedNominalComparisonGroups.filter(g => underlyingsInSubsector.has(g.underlying))
                if (filteredGroups.length === 0) return null

                const filteredTotals: Record<string, number> = {}
                sortedNominalComparisonAccounts.forEach(acct => {
                  filteredTotals[acct] = filteredGroups.reduce((sum, g) => sum + (g.accountPcts[acct] || 0), 0)
                })

                return (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {selectedSubsector.name} - Nominal Exp % ({filteredGroups.length} underlyings)
                    </h4>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-blue-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '120px' }}>Underlying</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '110px' }}>Type</th>
                            {sortedNominalComparisonAccounts.map(acctNum => (
                              <th key={acctNum} className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" style={{ width: '90px' }}>
                                {acctNum}
                              </th>
                            ))}
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase bg-blue-100" style={{ width: '90px' }}>
                              Wtd Avg
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredGroups.map((group) => {
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            const weightedAvg = calculateWeightedAvg(group.accountPcts)
                            return (
                              <tr key={group.underlying} className="hover:bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 font-medium text-gray-900">{group.underlying}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{typeLabel}</td>
                                {sortedNominalComparisonAccounts.map(acctNum => {
                                  const val = group.accountPcts[acctNum]
                                  const highlight = getHighlight(val, weightedAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-2 text-sm text-right font-medium ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                    }`}>
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right font-medium bg-blue-50">
                                  {formatPercent(weightedAvg)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-blue-100 font-semibold">
                          {(() => {
                            const subsectorWtdAvg = calculateWeightedAvg(filteredTotals)
                            return (
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                {sortedNominalComparisonAccounts.map(acctNum => {
                                  const val = filteredTotals[acctNum]
                                  const highlight = getHighlightTotal(val, subsectorWtdAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-3 text-right ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                                    }`}>
                                      {formatPercent(val)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-3 text-right text-gray-900 bg-blue-200">
                                  {formatPercent(subsectorWtdAvg)}
                                </td>
                              </tr>
                            )
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No position data available for comparison</p>
          )
        )}

        {activeTab === 'agg-delta-comparison' && (
          allAccountPortfolios.some(q => q.isLoading) ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : deltaComparisonData.groups.length > 0 ? (
            <div className="space-y-4">
              {/* Excluded Accounts Toggle */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Comparing {includedAccounts.length} accounts (Δ Adj Exp %)
                  {accounts?.accounts.some(a => a.exclude_from_aggregate) && (
                    <button
                      onClick={() => setShowExcludedAccounts(!showExcludedAccounts)}
                      className="ml-2 text-primary-600 hover:text-primary-700"
                    >
                      {showExcludedAccounts ? 'Hide' : 'Show'} excluded accounts
                    </button>
                  )}
                </div>
              </div>

              {/* Excluded Accounts Panel */}
              {showExcludedAccounts && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Account Exclusion Settings</h4>
                  <div className="flex flex-wrap gap-2">
                    {accounts?.accounts.map(account => (
                      <button
                        key={account.id}
                        onClick={() => toggleExclusionMutation.mutate(account.id)}
                        disabled={toggleExclusionMutation.isPending}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          account.exclude_from_aggregate
                            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        }`}
                      >
                        {account.exclude_from_aggregate ? <Plus className="inline h-3 w-3 mr-1" /> : <X className="inline h-3 w-3 mr-1" />}
                        {account.account_number}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Click to include/exclude accounts from comparison. Excluded accounts are grayed out.
                  </p>
                </div>
              )}

              {/* Subsector Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Subsector:</label>
                <select
                  value={selectedSubsectorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'create') {
                      setShowCreateSubsectorModal(true)
                    } else {
                      setSelectedSubsectorId(val ? Number(val) : null)
                    }
                  }}
                  className="input py-1 px-2 text-sm w-48"
                >
                  <option value="">None</option>
                  {subsectorsData?.subsectors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                  <option value="create">+ Create new...</option>
                </select>
              </div>

              {/* Delta Comparison Table */}
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                {/* Sort Controls */}
                <div className="flex items-center gap-4 mb-2 text-sm">
                  <span className="text-gray-500">Sort accounts by:</span>
                  <button
                    onClick={() => setDeltaComparisonAccountSort('number')}
                    className={`px-2 py-1 rounded ${deltaComparisonAccountSort === 'number' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Account Number
                  </button>
                  <button
                    onClick={() => setDeltaComparisonAccountSort('value')}
                    className={`px-2 py-1 rounded ${deltaComparisonAccountSort === 'value' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Portfolio Value
                  </button>
                </div>

                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    {/* Account Headers Row */}
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                      <ResizableHeader tableKey="agg-delta-comparison" columnKey="underlying" defaultWidth={120} className="text-left">Underlying</ResizableHeader>
                      <ResizableHeader tableKey="agg-delta-comparison" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                      {sortedDeltaComparisonAccounts.map(acctNum => (
                        <th
                          key={acctNum}
                          className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-100 ${
                            deltaComparisonRowSort.column === acctNum ? 'text-primary-700 bg-primary-50' : 'text-gray-500'
                          }`}
                          onClick={() => toggleDeltaComparisonRowSort(acctNum)}
                          style={{ width: '90px', minWidth: '90px' }}
                        >
                          {acctNum}
                          {deltaComparisonRowSort.column === acctNum && (
                            <span className="ml-1">{deltaComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                          )}
                        </th>
                      ))}
                      <th
                        className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-200 ${
                          deltaComparisonRowSort.column === 'wtdAvg' ? 'text-primary-700 bg-primary-100' : 'text-gray-500 bg-gray-100'
                        }`}
                        onClick={() => toggleDeltaComparisonRowSort('wtdAvg')}
                        style={{ width: '90px', minWidth: '90px' }}
                      >
                        Wtd Avg
                        {deltaComparisonRowSort.column === 'wtdAvg' && (
                          <span className="ml-1">{deltaComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }}>
                        Action
                      </th>
                      {selectedSubsector && (
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }} colSpan={2}>
                          {selectedSubsector.name}
                        </th>
                      )}
                    </tr>
                    {/* Calculated Total USD Info Row */}
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-1"></th>
                      <th className="px-4 py-1 text-left text-xs text-gray-400 font-normal">Portfolio Value</th>
                      <th className="px-4 py-1"></th>
                      {sortedDeltaComparisonAccounts.map(acctNum => (
                        <th key={acctNum} className="px-4 py-1 text-right text-xs text-gray-400 font-normal">
                          {formatCurrency(accountCalculatedTotals[acctNum])}
                        </th>
                      ))}
                      <th className="px-4 py-1 text-right text-xs text-gray-400 font-normal bg-gray-200">
                        {formatCurrency(Object.values(accountCalculatedTotals).reduce((a, b) => a + b, 0))}
                      </th>
                      <th className="px-4 py-1"></th>
                      {selectedSubsector && <th className="px-2 py-1" colSpan={2}></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedDeltaComparisonGroups.map((group) => {
                      const key = `delta-comp-${group.underlying}`
                      const isExpanded = expandedExposures.has(key)
                      const hasStocks = group.positions.some(p => !p.isOption)
                      const hasOptions = group.positions.some(p => p.isOption)
                      const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                      const weightedAvg = calculateWeightedAvg(group.accountPcts)

                      return (
                        <React.Fragment key={key}>
                          {/* Group Row */}
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleExpanded(key)}
                          >
                            <td className="px-4 py-3 text-sm">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{typeLabel}</td>
                            {sortedDeltaComparisonAccounts.map(acctNum => {
                              const val = group.accountPcts[acctNum]
                              const highlight = getHighlight(val, weightedAvg)
                              return (
                                <td
                                  key={acctNum}
                                  className={`px-4 py-3 text-sm text-right font-medium ${
                                    highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                    highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                    highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                    highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                  }`}
                                >
                                  {val !== null ? formatPercent(val) : '-'}
                                </td>
                              )
                            })}
                            <td className="px-4 py-3 text-sm text-right font-medium bg-gray-50">
                              {formatPercent(weightedAvg)}
                            </td>
                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/buysell/${encodeURIComponent(group.underlying)}?uploadId=${uploadId}&returnTab=${activeTab}`)}
                                className="p-1 rounded hover:bg-primary-100 text-primary-600"
                                title="Buy/Sell"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </button>
                            </td>
                            {selectedSubsector && (
                              <td className="px-2 py-3 text-center" colSpan={2} onClick={(e) => e.stopPropagation()}>
                                {underlyingsInSubsector.has(group.underlying) ? (
                                  <button
                                    onClick={() => removeFromSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-red-100 text-red-500"
                                    title={`Remove from ${selectedSubsector.name}`}
                                    disabled={removeFromSubsectorMutation.isPending}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => addToSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-green-100 text-green-500"
                                    title={`Add to ${selectedSubsector.name}`}
                                    disabled={addToSubsectorMutation.isPending}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>

                          {/* Expanded Position Rows */}
                          {isExpanded && group.positions.map((pos, idx) => {
                            const posWeightedAvg = calculateWeightedAvg(pos.accountPcts)
                            return (
                              <tr key={`${key}-${idx}`} className="bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                {sortedDeltaComparisonAccounts.map(acctNum => {
                                  const val = pos.accountPcts[acctNum]
                                  const highlight = getHighlight(val, posWeightedAvg)
                                  return (
                                    <td
                                      key={acctNum}
                                      className={`px-4 py-2 text-sm text-right ${
                                        highlight === 'high-heavy' ? 'bg-green-100 text-green-600' :
                                        highlight === 'high-light' ? 'bg-green-50 text-green-500' :
                                        highlight === 'low-heavy' ? 'bg-red-100 text-red-600' :
                                        highlight === 'low-light' ? 'bg-red-50 text-red-500' : 'text-gray-500'
                                      }`}
                                    >
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right text-gray-500 bg-gray-100">
                                  {formatPercent(posWeightedAvg)}
                                </td>
                                <td className="px-4 py-2"></td>
                                {selectedSubsector && <td className="px-2 py-2" colSpan={2}></td>}
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    {(() => {
                      const totalWtdAvg = calculateWeightedAvg(deltaComparisonTotals)
                      return (
                        <tr>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-900">Total</td>
                          <td className="px-4 py-3"></td>
                          {sortedDeltaComparisonAccounts.map(acctNum => {
                            const val = deltaComparisonTotals[acctNum]
                            const highlight = getHighlightTotal(val, totalWtdAvg)
                            return (
                              <td key={acctNum} className={`px-4 py-3 text-right ${
                                highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                              }`}>
                                {formatPercent(val)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-3 text-right text-gray-900 bg-gray-200">
                            {formatPercent(totalWtdAvg)}
                          </td>
                          <td className="px-4 py-3"></td>
                          {selectedSubsector && <td className="px-2 py-3" colSpan={2}></td>}
                        </tr>
                      )
                    })()}
                  </tfoot>
                </table>
              </div>

              {/* Filtered Subsector Table */}
              {selectedSubsector && (() => {
                const filteredGroups = sortedDeltaComparisonGroups.filter(g => underlyingsInSubsector.has(g.underlying))
                if (filteredGroups.length === 0) return null

                const filteredTotals: Record<string, number> = {}
                sortedDeltaComparisonAccounts.forEach(acct => {
                  filteredTotals[acct] = filteredGroups.reduce((sum, g) => sum + (g.accountPcts[acct] || 0), 0)
                })

                return (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {selectedSubsector.name} - Δ Adj Exp % ({filteredGroups.length} underlyings)
                    </h4>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-blue-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '120px' }}>Underlying</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '110px' }}>Type</th>
                            {sortedDeltaComparisonAccounts.map(acctNum => (
                              <th key={acctNum} className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" style={{ width: '90px' }}>
                                {acctNum}
                              </th>
                            ))}
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase bg-blue-100" style={{ width: '90px' }}>
                              Wtd Avg
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredGroups.map((group) => {
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            const weightedAvg = calculateWeightedAvg(group.accountPcts)
                            return (
                              <tr key={group.underlying} className="hover:bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 font-medium text-gray-900">{group.underlying}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{typeLabel}</td>
                                {sortedDeltaComparisonAccounts.map(acctNum => {
                                  const val = group.accountPcts[acctNum]
                                  const highlight = getHighlight(val, weightedAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-2 text-sm text-right font-medium ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                    }`}>
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right font-medium bg-blue-50">
                                  {formatPercent(weightedAvg)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-blue-100 font-semibold">
                          {(() => {
                            const subsectorWtdAvg = calculateWeightedAvg(filteredTotals)
                            return (
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                {sortedDeltaComparisonAccounts.map(acctNum => {
                                  const val = filteredTotals[acctNum]
                                  const highlight = getHighlightTotal(val, subsectorWtdAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-3 text-right ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                                    }`}>
                                      {formatPercent(val)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-3 text-right text-gray-900 bg-blue-200">
                                  {formatPercent(subsectorWtdAvg)}
                                </td>
                              </tr>
                            )
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No Δ adj exposure data available for comparison</p>
          )
        )}

        {activeTab === 'agg-nc-comparison' && (
          ncComparisonData.groups.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Compare NC (Net Cash) Exposure % across {ncComparisonData.accountNumbers.length} accounts.
                  Values highlighted when deviating from weighted avg: light (0.5-1%), heavy (1%+).
                </p>
                <div className="flex gap-2 text-sm">
                  <span className="text-gray-500">Sort accounts by:</span>
                  <button
                    onClick={() => setNcComparisonAccountSort('number')}
                    className={`px-2 py-1 rounded ${ncComparisonAccountSort === 'number' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Number
                  </button>
                  <button
                    onClick={() => setNcComparisonAccountSort('value')}
                    className={`px-2 py-1 rounded ${ncComparisonAccountSort === 'value' ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    Value
                  </button>
                </div>
              </div>

              {/* Subsector Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Subsector:</label>
                <select
                  value={selectedSubsectorId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'create') {
                      setShowCreateSubsectorModal(true)
                    } else {
                      setSelectedSubsectorId(val ? Number(val) : null)
                    }
                  }}
                  className="input py-1 px-2 text-sm w-48"
                >
                  <option value="">None</option>
                  {subsectorsData?.subsectors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                  <option value="create">+ Create new...</option>
                </select>
              </div>

              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    {/* Account Headers Row */}
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                      <ResizableHeader tableKey="agg-nc-comparison" columnKey="underlying" defaultWidth={120} className="text-left">Underlying</ResizableHeader>
                      <ResizableHeader tableKey="agg-nc-comparison" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                      {sortedNcComparisonAccounts.map(acctNum => (
                        <th
                          key={acctNum}
                          className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-100 ${
                            ncComparisonRowSort.column === acctNum ? 'text-primary-700 bg-primary-50' : 'text-gray-500'
                          }`}
                          onClick={() => toggleNcComparisonRowSort(acctNum)}
                          style={{ width: '90px', minWidth: '90px' }}
                        >
                          {acctNum}
                          {ncComparisonRowSort.column === acctNum && (
                            <span className="ml-1">{ncComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                          )}
                        </th>
                      ))}
                      <th
                        className={`px-4 py-2 text-right text-xs font-medium uppercase cursor-pointer hover:bg-gray-200 ${
                          ncComparisonRowSort.column === 'wtdAvg' ? 'text-primary-700 bg-primary-100' : 'text-gray-500 bg-gray-100'
                        }`}
                        onClick={() => toggleNcComparisonRowSort('wtdAvg')}
                        style={{ width: '90px', minWidth: '90px' }}
                      >
                        Wtd Avg
                        {ncComparisonRowSort.column === 'wtdAvg' && (
                          <span className="ml-1">{ncComparisonRowSort.direction === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }}>
                        Action
                      </th>
                      {selectedSubsector && (
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase" style={{ width: '70px', minWidth: '70px' }} colSpan={2}>
                          {selectedSubsector.name}
                        </th>
                      )}
                    </tr>
                    {/* Calculated Total USD Info Row */}
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-1"></th>
                      <th className="px-4 py-1 text-left text-xs text-gray-400 font-normal">Portfolio Value</th>
                      <th className="px-4 py-1"></th>
                      {sortedNcComparisonAccounts.map(acctNum => (
                        <th key={acctNum} className="px-4 py-1 text-right text-xs text-gray-400 font-normal">
                          {formatCurrency(accountCalculatedTotals[acctNum])}
                        </th>
                      ))}
                      <th className="px-4 py-1 text-right text-xs text-gray-400 font-normal bg-gray-200">
                        {formatCurrency(Object.values(accountCalculatedTotals).reduce((a, b) => a + b, 0))}
                      </th>
                      <th className="px-4 py-1"></th>
                      {selectedSubsector && <th className="px-2 py-1" colSpan={2}></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedNcComparisonGroups.map((group) => {
                      const key = `nc-comp-${group.underlying}`
                      const isExpanded = expandedExposures.has(key)
                      const hasStocks = group.positions.some(p => !p.isOption)
                      const hasOptions = group.positions.some(p => p.isOption)
                      const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                      const weightedAvg = calculateWeightedAvg(group.accountPcts)

                      return (
                        <React.Fragment key={key}>
                          {/* Group Row */}
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleExpanded(key)}
                          >
                            <td className="px-4 py-3 text-sm">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{typeLabel}</td>
                            {sortedNcComparisonAccounts.map(acctNum => {
                              const val = group.accountPcts[acctNum]
                              const highlight = getHighlight(val, weightedAvg)
                              return (
                                <td
                                  key={acctNum}
                                  className={`px-4 py-3 text-sm text-right font-medium ${
                                    highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                    highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                    highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                    highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                  }`}
                                >
                                  {val !== null ? formatPercent(val) : '-'}
                                </td>
                              )
                            })}
                            <td className="px-4 py-3 text-sm text-right font-medium bg-gray-50">
                              {formatPercent(weightedAvg)}
                            </td>
                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => navigate(`/buysell/${encodeURIComponent(group.underlying)}?uploadId=${uploadId}&returnTab=${activeTab}`)}
                                className="p-1 rounded hover:bg-primary-100 text-primary-600"
                                title="Buy/Sell"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </button>
                            </td>
                            {selectedSubsector && (
                              <td className="px-2 py-3 text-center" colSpan={2} onClick={(e) => e.stopPropagation()}>
                                {underlyingsInSubsector.has(group.underlying) ? (
                                  <button
                                    onClick={() => removeFromSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-red-100 text-red-500"
                                    title={`Remove from ${selectedSubsector.name}`}
                                    disabled={removeFromSubsectorMutation.isPending}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => addToSubsectorMutation.mutate({ subsectorId: selectedSubsector.id, underlying: group.underlying })}
                                    className="p-1 rounded hover:bg-green-100 text-green-500"
                                    title={`Add to ${selectedSubsector.name}`}
                                    disabled={addToSubsectorMutation.isPending}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>

                          {/* Expanded Position Rows */}
                          {isExpanded && group.positions.map((pos, idx) => {
                            const posWeightedAvg = calculateWeightedAvg(pos.accountPcts)
                            return (
                              <tr key={`${key}-${idx}`} className="bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                {sortedNcComparisonAccounts.map(acctNum => {
                                  const val = pos.accountPcts[acctNum]
                                  const highlight = getHighlight(val, posWeightedAvg)
                                  return (
                                    <td
                                      key={acctNum}
                                      className={`px-4 py-2 text-sm text-right ${
                                        highlight === 'high-heavy' ? 'bg-green-100 text-green-600' :
                                        highlight === 'high-light' ? 'bg-green-50 text-green-500' :
                                        highlight === 'low-heavy' ? 'bg-red-100 text-red-600' :
                                        highlight === 'low-light' ? 'bg-red-50 text-red-500' : 'text-gray-500'
                                      }`}
                                    >
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right text-gray-500 bg-gray-100">
                                  {formatPercent(posWeightedAvg)}
                                </td>
                                <td className="px-4 py-2"></td>
                                {selectedSubsector && <td className="px-2 py-2" colSpan={2}></td>}
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    {(() => {
                      const totalWtdAvg = calculateWeightedAvg(ncComparisonAccountTotals)
                      return (
                        <tr>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-gray-900">Total</td>
                          <td className="px-4 py-3"></td>
                          {sortedNcComparisonAccounts.map(acctNum => {
                            const val = ncComparisonAccountTotals[acctNum]
                            const highlight = getHighlightTotal(val, totalWtdAvg)
                            return (
                              <td key={acctNum} className={`px-4 py-3 text-right ${
                                highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                              }`}>
                                {formatPercent(val)}
                              </td>
                            )
                          })}
                          <td className="px-4 py-3 text-right text-gray-900 bg-gray-200">
                            {formatPercent(totalWtdAvg)}
                          </td>
                          <td className="px-4 py-3"></td>
                          {selectedSubsector && <td className="px-2 py-3" colSpan={2}></td>}
                        </tr>
                      )
                    })()}
                  </tfoot>
                </table>
              </div>

              {/* Filtered Subsector Table */}
              {selectedSubsector && (() => {
                const filteredGroups = sortedNcComparisonGroups.filter(g => underlyingsInSubsector.has(g.underlying))
                if (filteredGroups.length === 0) return null

                const filteredTotals: Record<string, number> = {}
                sortedNcComparisonAccounts.forEach(acct => {
                  filteredTotals[acct] = filteredGroups.reduce((sum, g) => sum + (g.accountPcts[acct] || 0), 0)
                })

                return (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {selectedSubsector.name} - NC Exp % ({filteredGroups.length} underlyings)
                    </h4>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200 table-fixed">
                        <thead className="bg-blue-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '120px' }}>Underlying</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" style={{ width: '110px' }}>Type</th>
                            {sortedNcComparisonAccounts.map(acctNum => (
                              <th key={acctNum} className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" style={{ width: '90px' }}>
                                {acctNum}
                              </th>
                            ))}
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase bg-blue-100" style={{ width: '90px' }}>
                              Wtd Avg
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredGroups.map((group) => {
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            const weightedAvg = calculateWeightedAvg(group.accountPcts)
                            return (
                              <tr key={group.underlying} className="hover:bg-gray-50">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2 font-medium text-gray-900">{group.underlying}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{typeLabel}</td>
                                {sortedNcComparisonAccounts.map(acctNum => {
                                  const val = group.accountPcts[acctNum]
                                  const highlight = getHighlight(val, weightedAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-2 text-sm text-right font-medium ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : ''
                                    }`}>
                                      {val !== null ? formatPercent(val) : '-'}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-2 text-sm text-right font-medium bg-blue-50">
                                  {formatPercent(weightedAvg)}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-blue-100 font-semibold">
                          {(() => {
                            const subsectorWtdAvg = calculateWeightedAvg(filteredTotals)
                            return (
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                {sortedNcComparisonAccounts.map(acctNum => {
                                  const val = filteredTotals[acctNum]
                                  const highlight = getHighlightTotal(val, subsectorWtdAvg)
                                  return (
                                    <td key={acctNum} className={`px-4 py-3 text-right ${
                                      highlight === 'high-heavy' ? 'bg-green-100 text-green-700' :
                                      highlight === 'high-light' ? 'bg-green-50 text-green-600' :
                                      highlight === 'low-heavy' ? 'bg-red-100 text-red-700' :
                                      highlight === 'low-light' ? 'bg-red-50 text-red-600' : 'text-gray-900'
                                    }`}>
                                      {formatPercent(val)}
                                    </td>
                                  )
                                })}
                                <td className="px-4 py-3 text-right text-gray-900 bg-blue-200">
                                  {formatPercent(subsectorWtdAvg)}
                                </td>
                              </tr>
                            )
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No NC exposure data available for comparison</p>
          )
        )}

        {activeTab === 'agg-nav' && (
          aggregateLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : aggregate && aggregate.nav_summary ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader<AggregatedNav> label="Asset Class" sortKey="asset_class" sortConfig={aggNavSort} onSort={setAggNavSort} className="text-left" tableKey="agg-nav" defaultWidth={180} />
                    <SortableHeader<AggregatedNav> label="Prior Total" sortKey="total_prior" sortConfig={aggNavSort} onSort={setAggNavSort} className="text-right" tableKey="agg-nav" defaultWidth={130} />
                    <SortableHeader<AggregatedNav> label="Current Total" sortKey="total_current" sortConfig={aggNavSort} onSort={setAggNavSort} className="text-right" tableKey="agg-nav" defaultWidth={130} />
                    <SortableHeader<AggregatedNav> label="Change" sortKey="total_change" sortConfig={aggNavSort} onSort={setAggNavSort} className="text-right" tableKey="agg-nav" defaultWidth={120} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortData(aggregate.nav_summary, aggNavSort).map((nav, idx) => (
                    <tr key={idx} className={`hover:bg-gray-50 ${nav.asset_class === 'Total' ? 'bg-gray-50 font-semibold' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-900">{nav.asset_class}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(nav.total_prior)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(nav.total_current)}</td>
                      <td className={`px-4 py-3 text-sm text-right ${
                        nav.total_change && nav.total_change > 0 ? 'text-green-600' :
                        nav.total_change && nav.total_change < 0 ? 'text-red-600' : ''
                      }`}>
                        {formatCurrency(nav.total_change)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No aggregate NAV data available</p>
          )
        )}

        {activeTab === 'agg-cash' && (
          aggregateLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : aggregate && aggregate.cash_summary ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader<{ line_item: string; currency: string | null; total: number | null }> label="Line Item" sortKey="line_item" sortConfig={aggCashSort} onSort={setAggCashSort} className="text-left" tableKey="agg-cash" defaultWidth={200} />
                    <SortableHeader<{ line_item: string; currency: string | null; total: number | null }> label="Currency" sortKey="currency" sortConfig={aggCashSort} onSort={setAggCashSort} className="text-left" tableKey="agg-cash" defaultWidth={100} />
                    <SortableHeader<{ line_item: string; currency: string | null; total: number | null }> label="Total" sortKey="total" sortConfig={aggCashSort} onSort={setAggCashSort} className="text-right" tableKey="agg-cash" defaultWidth={130} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortData(aggregate.cash_summary, aggCashSort).map((cash, idx) => (
                    <tr key={idx} className={`hover:bg-gray-50 ${cash.line_item === 'Ending Cash' ? 'bg-green-50 font-semibold border-l-4 border-green-500' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {cash.line_item}
                        {cash.line_item === 'Ending Cash' && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            Cash Total
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{cash.currency || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(cash.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No aggregate cash data available</p>
          )
        )}

        {activeTab === 'acct-positions' && (
          portfolioLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : portfolio ? (
            (() => {
              const positionsWithUsd = portfolio.open_positions
                .filter((p) => p.row_type === 'Data' && p.symbol)
                .map(pos => ({ ...pos, value_usd: convertToUsd(pos.value, pos.currency, fxRateMap) }))
              const equityOptions = positionsWithUsd.filter(p => p.asset_category !== 'Futures')
              const futures = positionsWithUsd.filter(p => p.asset_category === 'Futures')

              const eqTotals = equityOptions.reduce((acc, p) => ({
                value: acc.value + (Number(p.value) || 0),
                valueUsd: acc.valueUsd + (p.value_usd || 0),
                nominal: acc.nominal + (Number(p.nominal_exposure) || 0),
                deltaAdj: acc.deltaAdj + (Number(p.delta_adjusted_exposure) || 0),
              }), { value: 0, valueUsd: 0, nominal: 0, deltaAdj: 0 })

              const futTotals = futures.reduce((acc, p) => ({
                value: acc.value + (Number(p.value) || 0),
                valueUsd: acc.valueUsd + (p.value_usd || 0),
                nominal: acc.nominal + (Number(p.nominal_exposure) || 0),
                deltaAdj: acc.deltaAdj + (Number(p.delta_adjusted_exposure) || 0),
              }), { value: 0, valueUsd: 0, nominal: 0, deltaAdj: 0 })

              return (
                <div className="space-y-6">
                  {/* Equity & Options Positions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Equity & Options</h3>
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <SortableHeader<OpenPositionWithUsd> label="Account" sortKey="account_number" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-positions" defaultWidth={90} />
                            <SortableHeader<OpenPositionWithUsd> label="Symbol" sortKey="symbol" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-positions" defaultWidth={180} />
                            <ResizableHeader tableKey="acct-positions" columnKey="underlying" defaultWidth={100} className="text-left">Underlying</ResizableHeader>
                            <SortableHeader<OpenPositionWithUsd> label="Category" sortKey="asset_category" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-positions" defaultWidth={160} />
                            <SortableHeader<OpenPositionWithUsd> label="Qty" sortKey="quantity" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={80} />
                            <SortableHeader<OpenPositionWithUsd> label="Price" sortKey="close_price" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={100} />
                            <SortableHeader<OpenPositionWithUsd> label="Value" sortKey="value" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={110} />
                            <SortableHeader<OpenPositionWithUsd> label="Currency" sortKey="currency" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-positions" defaultWidth={80} />
                            <SortableHeader<OpenPositionWithUsd> label="Value (USD)" sortKey="value_usd" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={110} />
                            <SortableHeader<OpenPositionWithUsd> label="Nominal Exp" sortKey="nominal_exposure" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={110} />
                            <SortableHeader<OpenPositionWithUsd> label="Delta-Adj Exp" sortKey="delta_adjusted_exposure" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={110} />
                            <SortableHeader<OpenPositionWithUsd> label="IV" sortKey="implied_volatility" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-positions" defaultWidth={70} />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortData(equityOptions, acctPositionsSort).map((pos) => (
                            <tr key={pos.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600 font-mono">{pos.account_number || '-'}</td>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-900">{pos.symbol}</p>
                                  <p className="text-xs text-gray-500 truncate max-w-[200px]">{pos.description}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{getUnderlying(pos.symbol, pos.asset_category) || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{pos.asset_category}</td>
                              <td className="px-4 py-3 text-sm text-right">{formatQuantity(pos.quantity)}</td>
                              <td className="px-4 py-3 text-sm text-right">{formatCurrency(pos.close_price)}</td>
                              <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(pos.value)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{pos.currency || '-'}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.value_usd)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.nominal_exposure)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.delta_adjusted_exposure)}</td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">{pos.implied_volatility != null ? `${(pos.implied_volatility * 100).toFixed(1)}%` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-semibold">
                          <tr>
                            <td className="px-4 py-3 text-gray-900">Total Equity & Options</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right">-</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right">{formatCurrency(calculatedTotalUsd !== null ? calculatedTotalUsd - summary.cash : eqTotals.valueUsd)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.nominal)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.deltaAdj)}</td>
                            <td className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Futures Positions */}
                  {futures.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Futures</h3>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <SortableHeader<OpenPositionWithUsd> label="Account" sortKey="account_number" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-futures" defaultWidth={90} />
                              <SortableHeader<OpenPositionWithUsd> label="Symbol" sortKey="symbol" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-futures" defaultWidth={180} />
                              <SortableHeader<OpenPositionWithUsd> label="Qty" sortKey="quantity" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={80} />
                              <SortableHeader<OpenPositionWithUsd> label="Price" sortKey="close_price" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={100} />
                              <SortableHeader<OpenPositionWithUsd> label="Value" sortKey="value" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={110} />
                              <SortableHeader<OpenPositionWithUsd> label="Currency" sortKey="currency" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-left" tableKey="acct-futures" defaultWidth={80} />
                              <SortableHeader<OpenPositionWithUsd> label="Value (USD)" sortKey="value_usd" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={110} />
                              <SortableHeader<OpenPositionWithUsd> label="Nominal Exp" sortKey="nominal_exposure" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={110} />
                              <SortableHeader<OpenPositionWithUsd> label="Delta-Adj Exp" sortKey="delta_adjusted_exposure" sortConfig={acctPositionsSort} onSort={setAcctPositionsSort} className="text-right" tableKey="acct-futures" defaultWidth={110} />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {sortData(futures, acctPositionsSort).map((pos) => (
                              <tr key={pos.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{pos.account_number || '-'}</td>
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="font-medium text-gray-900">{pos.symbol}</p>
                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{pos.description}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-right">{formatQuantity(pos.quantity)}</td>
                                <td className="px-4 py-3 text-sm text-right">{formatCurrency(pos.close_price)}</td>
                                <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(pos.value)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{pos.currency || '-'}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.value_usd)}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.nominal_exposure)}</td>
                                <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(pos.delta_adjusted_exposure)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 font-semibold">
                            <tr>
                              <td className="px-4 py-3 text-gray-900">Total Futures</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right">-</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right">{formatCurrency(futTotals.valueUsd)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.nominal)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.deltaAdj)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )
            })()
          ) : (
            <p className="text-gray-500 text-center py-8">No positions data available</p>
          )
        )}

        {activeTab === 'acct-exposures' && (
          portfolioLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : portfolio ? (
            (() => {
              const { equityAndOptions, futures } = groupPositionsByUnderlying(portfolio.open_positions, fxRateMap, optionUnderlyingMap)
              // Calculate section totals
              const eqTotals = equityAndOptions.reduce((acc, g) => ({
                value: acc.value + (g.totalValueUsd || 0),
                nominal: acc.nominal + (g.totalNominalExposure || 0),
                deltaAdj: acc.deltaAdj + (g.totalDeltaAdjustedExposure || 0),
                ncExp: acc.ncExp + (g.totalNcExposure || 0),
                posPct: acc.posPct + (g.totalPositionPct || 0),
                deltaExpPct: acc.deltaExpPct + (g.totalDeltaExposurePct || 0),
                ncExpPct: acc.ncExpPct + (g.totalNcExposurePct || 0),
              }), { value: 0, nominal: 0, deltaAdj: 0, ncExp: 0, posPct: 0, deltaExpPct: 0, ncExpPct: 0 })
              const futTotals = futures.reduce((acc, g) => ({
                value: acc.value + (g.totalValueUsd || 0),
                nominal: acc.nominal + (g.totalNominalExposure || 0),
                deltaAdj: acc.deltaAdj + (g.totalDeltaAdjustedExposure || 0),
                ncExp: acc.ncExp + (g.totalNcExposure || 0),
                posPct: acc.posPct + (g.totalPositionPct || 0),
                deltaExpPct: acc.deltaExpPct + (g.totalDeltaExposurePct || 0),
                ncExpPct: acc.ncExpPct + (g.totalNcExposurePct || 0),
              }), { value: 0, nominal: 0, deltaAdj: 0, ncExp: 0, posPct: 0, deltaExpPct: 0, ncExpPct: 0 })

              // Collect all account numbers for the totals
              const allEqAccounts = new Set<string>()
              equityAndOptions.forEach(g => g.accountNumbers.forEach(a => allEqAccounts.add(a)))
              const allFutAccounts = new Set<string>()
              futures.forEach(g => g.accountNumbers.forEach(a => allFutAccounts.add(a)))

              // Group by currency and type
              const byCurrency = groupPositionsByCurrency(portfolio.open_positions, fxRateMap)
              const byType = groupPositionsByType(portfolio.open_positions, fxRateMap)

              return (
                <div className="space-y-4">
                  {/* By Underlying Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('acct-byUnderlying')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('acct-byUnderlying') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Underlying
                      </h3>
                      <span className="text-sm text-gray-500">{equityAndOptions.length + futures.length} groups</span>
                    </button>
                    {expandedSections.has('acct-byUnderlying') && (
                    <div className="p-4 space-y-6">
                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <SortableHeader<GroupedExposure> label="Underlying" sortKey="underlying" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-left" tableKey="acct-exposures" defaultWidth={120} />
                            <ResizableHeader tableKey="acct-exposures" columnKey="type" defaultWidth={110} className="text-left">Type</ResizableHeader>
                            <ResizableHeader tableKey="acct-exposures" columnKey="account" defaultWidth={90} className="text-left">Account</ResizableHeader>
                            <SortableHeader<GroupedExposure> label="Quantity" sortKey="totalQuantity" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={80} />
                            <ResizableHeader tableKey="acct-exposures" columnKey="price" defaultWidth={90} className="text-right">Price</ResizableHeader>
                            <ResizableHeader tableKey="acct-exposures" columnKey="value" defaultWidth={100} className="text-right">Value</ResizableHeader>
                            <ResizableHeader tableKey="acct-exposures" columnKey="ccy" defaultWidth={50} className="text-left">Ccy</ResizableHeader>
                            <SortableHeader<GroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={70} />
                            <SortableHeader<GroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Nominal Exp %" sortKey="totalNominalExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={95} />
                            <SortableHeader<GroupedExposure> label="Δ Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="Δ Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={95} />
                            <SortableHeader<GroupedExposure> label="NC Exp" sortKey="totalNcExposure" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={100} />
                            <SortableHeader<GroupedExposure> label="NC Exp %" sortKey="totalNcExposurePct" sortConfig={exposuresSort} onSort={setExposuresSort} className="text-right" tableKey="acct-exposures" defaultWidth={85} />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {sortData(equityAndOptions, exposuresSort).map((group) => {
                            const key = `acct-${group.underlying}`
                            const isExpanded = expandedExposures.has(key)
                            const hasStocks = group.positions.some(p => !p.isOption)
                            const hasOptions = group.positions.some(p => p.isOption)
                            const typeLabel = hasStocks && hasOptions ? 'Stock + Options' : (hasOptions ? 'Options' : 'Stock')
                            return (
                              <React.Fragment key={key}>
                                <tr
                                  className="hover:bg-gray-50 cursor-pointer"
                                  onClick={() => toggleExpanded(key)}
                                >
                                  <td className="px-4 py-3 text-sm">
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{typeLabel}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-right"></td>
                                  <td className="px-4 py-3 text-sm text-gray-600"></td>
                                  <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalNominalExposure || 0) / calculatedTotalUsd : null)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                  <td className={`px-4 py-3 text-sm text-right ${group.hasNcExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {group.hasNcExposureError ? 'Unlimited Loss' : formatCurrency(group.totalNcExposure)}
                                  </td>
                                  <td className={`px-4 py-3 text-sm text-right ${group.hasNcExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                    {group.hasNcExposureError ? 'Potential' : formatPercent(calculatedTotalUsd ? (group.totalNcExposure || 0) / calculatedTotalUsd : null)}
                                  </td>
                                </tr>
                                {isExpanded && group.positions.map((pos, idx) => (
                                  <tr key={`${key}-${idx}`} className="bg-gray-50">
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{pos.isOption ? 'Option' : 'Stock'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.price)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.nominalExposurePct)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                    <td className={`px-4 py-2 text-sm text-right ${pos.ncExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {pos.ncExposureError ? 'Unlimited Loss' : formatCurrency(pos.ncExposure)}
                                    </td>
                                    <td className={`px-4 py-2 text-sm text-right ${pos.ncExposureError ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {pos.ncExposureError ? 'Potential' : formatPercent(pos.ncExposurePct)}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                        <tfoot className="bg-gray-100 font-semibold">
                          <tr>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-gray-900">Total Equity & Options</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(allEqAccounts)}</td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3"></td>
                            <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(eqTotals.value)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.value / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.nominal)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.nominal / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.deltaAdj)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.deltaAdj / calculatedTotalUsd : null)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(eqTotals.ncExp)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? eqTotals.ncExp / calculatedTotalUsd : null)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                  {/* Futures */}
                  {futures.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Futures</h3>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                              <SortableHeader<FuturesGroupedExposure> label="Underlying" sortKey="underlying" sortConfig={futuresSort} onSort={setFuturesSort} className="text-left" tableKey="acct-futures-exp" defaultWidth={120} />
                              <ResizableHeader tableKey="acct-futures-exp" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                              <SortableHeader<FuturesGroupedExposure> label="Quantity" sortKey="totalQuantity" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="acct-futures-exp" defaultWidth={80} />
                              <ResizableHeader tableKey="acct-futures-exp" columnKey="price" defaultWidth={90} className="text-right">Price</ResizableHeader>
                              <ResizableHeader tableKey="acct-futures-exp" columnKey="value" defaultWidth={100} className="text-right">Value</ResizableHeader>
                              <ResizableHeader tableKey="acct-futures-exp" columnKey="ccy" defaultWidth={50} className="text-left">Ccy</ResizableHeader>
                              <SortableHeader<FuturesGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="acct-futures-exp" defaultWidth={100} />
                              <SortableHeader<FuturesGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="acct-futures-exp" defaultWidth={100} />
                              <SortableHeader<FuturesGroupedExposure> label="Nominal Exp %" sortKey="totalNominalExposurePct" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="acct-futures-exp" defaultWidth={95} />
                              <SortableHeader<FuturesGroupedExposure> label="Δ Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={futuresSort} onSort={setFuturesSort} className="text-right" tableKey="acct-futures-exp" defaultWidth={100} />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {sortData(futures, futuresSort).map((group) => {
                              const key = `acct-futures-${group.underlying}`
                              const isExpanded = expandedExposures.has(key)
                              return (
                                <React.Fragment key={key}>
                                  <tr
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => toggleExpanded(key)}
                                  >
                                    <td className="px-4 py-3 text-sm">
                                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </td>
                                    <td className="px-4 py-3 font-medium text-gray-900">{group.underlying}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-right"></td>
                                    <td className="px-4 py-3 text-sm text-gray-600"></td>
                                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                  </tr>
                                  {isExpanded && group.positions.map((pos, idx) => (
                                    <tr key={`${key}-${idx}`} className="bg-gray-50">
                                      <td className="px-4 py-2"></td>
                                      <td className="px-4 py-2 text-sm text-gray-600 pl-8">{pos.symbol}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.price)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                      <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                          <tfoot className="bg-gray-100 font-semibold">
                            <tr>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-gray-900">Total Futures</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(allFutAccounts)}</td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3"></td>
                              <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(futTotals.value)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.nominal)}</td>
                              <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(futTotals.deltaAdj)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                    </div>
                    )}
                  </div>

                  {/* By Currency Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('acct-byCurrency')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('acct-byCurrency') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Currency (Equity & Options only)
                      </h3>
                      <span className="text-sm text-gray-500">{byCurrency.length} currencies</span>
                    </button>
                    {expandedSections.has('acct-byCurrency') && (
                      <div className="p-4">
                        <p className="text-sm text-amber-600 mb-3 bg-amber-50 p-2 rounded">
                          Note: If FX rates have moved since the report was generated, these totals may not match the overall totals.
                        </p>
                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                                <SortableHeader<CurrencyGroupedExposure> label="Currency" sortKey="currency" sortConfig={currencySort} onSort={setCurrencySort} className="text-left" tableKey="acct-by-currency" defaultWidth={100} />
                                <ResizableHeader tableKey="acct-by-currency" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                                <ResizableHeader tableKey="acct-by-currency" columnKey="positions" defaultWidth={80} className="text-right">Positions</ResizableHeader>
                                <SortableHeader<CurrencyGroupedExposure> label="Value" sortKey="totalValue" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={70} />
                                <SortableHeader<CurrencyGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={100} />
                                <SortableHeader<CurrencyGroupedExposure> label="Delta-Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={110} />
                                <SortableHeader<CurrencyGroupedExposure> label="Delta-Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={currencySort} onSort={setCurrencySort} className="text-right" tableKey="acct-by-currency" defaultWidth={105} />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {sortData(byCurrency, currencySort).map((group) => {
                                const key = `acct-currency-${group.currency}`
                                const isExpanded = expandedExposures.has(key)
                                return (
                                  <React.Fragment key={group.currency}>
                                    <tr
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => toggleExpanded(key)}
                                    >
                                      <td className="px-4 py-3 text-sm">
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </td>
                                      <td className="px-4 py-3 font-medium text-gray-900">{group.currency}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">{group.positionCount}</td>
                                      <td className="px-4 py-3 text-sm text-right">{formatValue(group.totalValue)}</td>
                                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                    </tr>
                                    {isExpanded && group.positions.map((pos, idx) => (
                                      <tr key={`${key}-${idx}`} className="bg-gray-50">
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-sm text-gray-600 pl-4">{pos.symbol}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatValue(pos.value)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                            <tfoot className="bg-gray-100 font-semibold">
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{byCurrency.reduce((sum, g) => sum + g.positionCount, 0)}</td>
                                <td className="px-4 py-3 text-right">-</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byCurrency.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0) / calculatedTotalUsd : null)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalNominalExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byCurrency.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byCurrency.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0) / calculatedTotalUsd : null)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* By Type Section - Collapsible */}
                  <div className="border rounded-lg">
                    <button
                      onClick={() => toggleSection('acct-byType')}
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {expandedSections.has('acct-byType') ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        By Type
                      </h3>
                      <span className="text-sm text-gray-500">{byType.length} types</span>
                    </button>
                    {expandedSections.has('acct-byType') && (
                      <div className="p-4">
                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                                <SortableHeader<TypeGroupedExposure> label="Asset Type" sortKey="assetType" sortConfig={typeSort} onSort={setTypeSort} className="text-left" tableKey="acct-by-type" defaultWidth={160} />
                                <ResizableHeader tableKey="acct-by-type" columnKey="accounts" defaultWidth={90} className="text-left">Accounts</ResizableHeader>
                                <ResizableHeader tableKey="acct-by-type" columnKey="positions" defaultWidth={80} className="text-right">Positions</ResizableHeader>
                                <ResizableHeader tableKey="acct-by-type" columnKey="currencies" defaultWidth={90} className="text-left">Currencies</ResizableHeader>
                                <SortableHeader<TypeGroupedExposure> label="Value (USD)" sortKey="totalValueUsd" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="acct-by-type" defaultWidth={110} />
                                <SortableHeader<TypeGroupedExposure> label="Pos %" sortKey="totalPositionPct" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="acct-by-type" defaultWidth={70} />
                                <SortableHeader<TypeGroupedExposure> label="Nominal Exp" sortKey="totalNominalExposure" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="acct-by-type" defaultWidth={100} />
                                <SortableHeader<TypeGroupedExposure> label="Delta-Adj Exp" sortKey="totalDeltaAdjustedExposure" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="acct-by-type" defaultWidth={110} />
                                <SortableHeader<TypeGroupedExposure> label="Delta-Adj Exp %" sortKey="totalDeltaExposurePct" sortConfig={typeSort} onSort={setTypeSort} className="text-right" tableKey="acct-by-type" defaultWidth={105} />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {sortData(byType, typeSort).map((group) => {
                                const key = `acct-type-${group.assetType}`
                                const isExpanded = expandedExposures.has(key)
                                return (
                                  <React.Fragment key={group.assetType}>
                                    <tr
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => toggleExpanded(key)}
                                    >
                                      <td className="px-4 py-3 text-sm">
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </td>
                                      <td className="px-4 py-3 font-medium text-gray-900">{group.assetType}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{formatAccounts(group.accountNumbers)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-600">{group.positionCount}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600">{group.currencies.size > 1 ? `${group.currencies.size} currencies` : Array.from(group.currencies)[0] || '-'}</td>
                                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(group.totalValueUsd)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalValueUsd || 0) / calculatedTotalUsd : null)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalNominalExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatCurrency(group.totalDeltaAdjustedExposure)}</td>
                                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatPercent(calculatedTotalUsd ? (group.totalDeltaAdjustedExposure || 0) / calculatedTotalUsd : null)}</td>
                                    </tr>
                                    {isExpanded && group.positions.map((pos, idx) => (
                                      <tr key={`${key}-${idx}`} className="bg-gray-50">
                                        <td className="px-4 py-2"></td>
                                        <td className="px-4 py-2 text-sm text-gray-600 pl-4">{pos.symbol}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{formatAccounts(pos.accountNumbers)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatQuantity(pos.quantity)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">{pos.currency || '-'}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(pos.valueUsd)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.positionPct)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.nominalExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatCurrency(pos.deltaAdjustedExposure)}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-500">{formatPercent(pos.deltaExposurePct)}</td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                            <tfoot className="bg-gray-100 font-semibold">
                              <tr>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-gray-900">Total</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{byType.reduce((sum, g) => sum + g.positionCount, 0)}</td>
                                <td className="px-4 py-3"></td>
                                <td className="px-4 py-3 text-right">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byType.reduce((sum, g) => sum + (g.totalValueUsd || 0), 0) / calculatedTotalUsd : null)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalNominalExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(byType.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0))}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{formatPercent(calculatedTotalUsd ? byType.reduce((sum, g) => sum + (g.totalDeltaAdjustedExposure || 0), 0) / calculatedTotalUsd : null)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()
          ) : (
            <p className="text-gray-500 text-center py-8">No exposure data available</p>
          )
        )}

        {activeTab === 'acct-nav' && (
          portfolioLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : portfolio ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader<NetAssetValue> label="Asset Class" sortKey="asset_class" sortConfig={acctNavSort} onSort={setAcctNavSort} className="text-left" tableKey="acct-nav" defaultWidth={180} />
                    <SortableHeader<NetAssetValue> label="Prior" sortKey="prior_total" sortConfig={acctNavSort} onSort={setAcctNavSort} className="text-right" tableKey="acct-nav" defaultWidth={120} />
                    <SortableHeader<NetAssetValue> label="Current" sortKey="current_total" sortConfig={acctNavSort} onSort={setAcctNavSort} className="text-right" tableKey="acct-nav" defaultWidth={120} />
                    <SortableHeader<NetAssetValue> label="Change" sortKey="change" sortConfig={acctNavSort} onSort={setAcctNavSort} className="text-right" tableKey="acct-nav" defaultWidth={120} />
                    <SortableHeader<NetAssetValue> label="Type" sortKey="row_type" sortConfig={acctNavSort} onSort={setAcctNavSort} className="text-center" tableKey="acct-nav" defaultWidth={80} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortData(portfolio.net_asset_values, acctNavSort).map((nav) => (
                    <tr key={nav.id} className={`hover:bg-gray-50 ${nav.row_type === 'Total' ? 'bg-gray-50 font-semibold' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-900">{nav.asset_class}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(nav.prior_total)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(nav.current_total)}</td>
                      <td className={`px-4 py-3 text-sm text-right ${
                        nav.change && nav.change > 0 ? 'text-green-600' :
                        nav.change && nav.change < 0 ? 'text-red-600' : ''
                      }`}>
                        {formatCurrency(nav.change)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          nav.row_type === 'Total' ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {nav.row_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No NAV data available</p>
          )
        )}

        {activeTab === 'acct-cash' && (
          portfolioLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : portfolio ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader<CashReport> label="Line Item" sortKey="line_item" sortConfig={acctCashSort} onSort={setAcctCashSort} className="text-left" tableKey="acct-cash" defaultWidth={200} />
                    <SortableHeader<CashReport> label="Currency" sortKey="currency" sortConfig={acctCashSort} onSort={setAcctCashSort} className="text-left" tableKey="acct-cash" defaultWidth={100} />
                    <SortableHeader<CashReport> label="Total" sortKey="total" sortConfig={acctCashSort} onSort={setAcctCashSort} className="text-right" tableKey="acct-cash" defaultWidth={120} />
                    <SortableHeader<CashReport> label="Securities" sortKey="securities" sortConfig={acctCashSort} onSort={setAcctCashSort} className="text-right" tableKey="acct-cash" defaultWidth={110} />
                    <SortableHeader<CashReport> label="Futures" sortKey="futures" sortConfig={acctCashSort} onSort={setAcctCashSort} className="text-right" tableKey="acct-cash" defaultWidth={100} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortData(portfolio.cash_reports, acctCashSort).map((cash) => {
                    const isEndingCash = isEndingCashRow(cash)
                    return (
                      <tr
                        key={cash.id}
                        className={`hover:bg-gray-50 ${
                          isEndingCash
                            ? 'bg-green-50 font-semibold border-l-4 border-green-500'
                            : cash.row_type === 'Total'
                            ? 'bg-gray-50 font-semibold'
                            : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {cash.line_item}
                          {isEndingCash && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              Cash Total
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cash.currency || cash.currency_summary || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(cash.total)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(cash.securities)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(cash.futures)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No cash report data available</p>
          )
        )}
      </div>

      {/* Create Subsector Modal */}
      {showCreateSubsectorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Subsector</h3>
            <input
              type="text"
              value={newSubsectorName}
              onChange={(e) => setNewSubsectorName(e.target.value)}
              placeholder="Subsector name"
              className="input w-full mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newSubsectorName.trim()) {
                  createSubsectorMutation.mutate(newSubsectorName.trim())
                }
                if (e.key === 'Escape') {
                  setShowCreateSubsectorModal(false)
                  setNewSubsectorName('')
                }
              }}
            />
            {createSubsectorMutation.isError && (
              <p className="text-red-500 text-sm mb-4">
                {(createSubsectorMutation.error as Error)?.message || 'Failed to create subsector'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateSubsectorModal(false)
                  setNewSubsectorName('')
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newSubsectorName.trim()) {
                    createSubsectorMutation.mutate(newSubsectorName.trim())
                  }
                }}
                disabled={!newSubsectorName.trim() || createSubsectorMutation.isPending}
                className="btn btn-primary"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Wrap with ColumnResizeProvider
export default function Portfolio() {
  return (
    <ColumnResizeProvider>
      <PortfolioContent />
    </ColumnResizeProvider>
  )
}
